/* eslint-disable max-params */
/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/no-unknown-property */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTableNav } from '@pypestream/table-nav';
import { virtualizerMeasureElement } from '../../utils/virtualizer-measure-element';
import {
  ColumnFiltersState,
  FilterFn,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  SortingFn,
  sortingFns,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import { debounce } from 'lodash';
import { observer } from 'mobx-react-lite';
import { reaction } from 'mobx';
import {
  RankingInfo,
  rankItem,
  compareItems,
} from '@tanstack/match-sorter-utils';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  memo,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  Suspense,
} from 'react';
import { Panel, Group, Separator } from 'react-resizable-panels';
import {
  useResizeDetector,
  useResizeDetectorProps,
} from 'react-resize-detector';
import {
  Button,
  Icon,
  TextBody,
  Tooltip,
  IconButton,
  Search,
  Divider,
  SearchPropsWithRef,
  TextSubtitle,
  Stack,
  sendToast,
} from '@pypestream/design-system';
import {
  DragDropEvents,
  DragDropProvider,
  KeyboardSensor,
  PointerSensor,
} from '@dnd-kit/react';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { PointerActivationConstraints } from '@dnd-kit/dom';
import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';

import { DragShadowPortal } from './src/drag-shadow-portal';
import { arrayMoveImmutable, calculateSelectedRowsHeight } from '../../utils';
import {
  tableStore,
  TableRow,
  emitOnce,
  TableNode,
  NodeColumns,
  ColumnsWidths,
  HIDDEN_COLUMNS,
  RevealRange,
} from '../../stores/table-store';
import { computeRevealScrollDelta } from '../../stores/utils/reveal-scroll';
import { getColumnEditKind } from '../../stores/utils/column-edit-kind';
import {
  cellToFocusOnClosedSelectEscape,
  isEditableSelectTarget,
  isOpenEditableSelect,
} from '../../stores/utils/is-editable-select-target';
import { shouldAutoOpenExpandedCellView } from '../../stores/utils/should-auto-open-expanded-cell-view';
import { isJsonCapableColumn } from '../../stores/utils/is-json-capable-column';
import {
  EXPANDED_CELL_VIEW_MIN_WIDTH,
  EXPANDED_CELL_VIEW_MAX_WIDTH,
} from '../../stores/expanded-cell-view-width-storage';
import { nodeColumnDisplayNames } from '../../stores/utils/column-display-names';
import { NodeAnswerRequiredCheckbox } from './node-answer-required-checkbox';
import { NodeBehaviorsMultiSelect } from './node-behaviors-multi-select';
import { NodeRichAssetSingleSelect } from './node-rich-asset-single-select';
import { NodeCommandSelect } from './node-command-select';
import { EditableTableCell } from './editable-table-cell-input';
import { EditableTableCellTextArea } from './editable-table-cell-textarea';
import { RichAssetContentCell } from './node-rich-asset-content-cell';
import { NodeTypeCell } from './node-type-cell';
import { Cell } from './src/cell';
import { useCloseMenuOnScroll } from './src/use-close-menu-on-scroll';
import { ControlsBar } from './src/controls-bar';
import { DraggableRow } from './src/draggable-row';
import { HeaderCell } from './src/header-cell';
import { SupervisorPanel } from './supervisor/supervisor-panel';
import { ResourceLibPanel } from './resource-lib/resource-lib-panel';
import { ResourceLibraryFab } from './resource-lib/resource-library-fab';
import { ExpandedCellViewPanel } from './expanded-cell-view/expanded-cell-view-panel';
import { NodePropertyPanel } from './node-property-panel/node-property-panel';
import { wireOneSidebarPanelAtATime } from './expanded-cell-view/wire-one-panel-at-a-time';
// PSTUDIO-1433: grip icon for the resize handle — the same asset (and same
// import form) the toolbar resize handle used before PSTUDIO-3910 removed it.
import svgHandles from './icon-handles.svg?react';
import { shouldToggleExpandedCellView } from './expanded-cell-view/should-toggle';
import { shouldFocusPanelSelectOnKeyboardOpen } from './expanded-cell-view/panel-autofocus-target';
import { shouldGridHandleTabCommit } from './should-grid-handle-tab-commit';
import { KeyboardShortcutsPanel } from './keyboard-shortcuts/keyboard-shortcuts-panel';
import { shouldToggleKeyboardShortcutsPanel } from './keyboard-shortcuts/should-toggle-shortcuts';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import {
  useProStudioViewerRole,
  useBobcatFlag,
  useUploadPythonFiles,
} from '../../hooks';
import {
  scriptNamesFor,
  uploadResourceLibScripts,
} from './resource-lib/upload-resource-lib-scripts';
import { columnDefaultSizes } from '../../stores';
import { getCellKey } from './utils';
import './table.scss';

export type ButtonOption = {
  label: string;
  dest: number;
};

const resizeDetectorConfig: useResizeDetectorProps<any> = {
  handleHeight: false,
  refreshMode: 'debounce',
  refreshRate: 50,
};

// Import serializeValue from header-cell or redefine here for consistency
const serializeValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object' && value !== null) return JSON.stringify(value);
  if (value === undefined || value === null) return '';
  return String(value);
};

// Declare the multiValue filter function type
declare module '@tanstack/table-core' {
  interface FilterFns {
    multiValue: FilterFn<any>;
    fuseFilter: FilterFn<unknown>;
  }
}

declare module '@tanstack/react-table' {
  // add fuzzy filter to the filterFns
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
    fuseFilter: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

// Multi-value filter function for all columns
const multiValueFilter = (
  row: Row<any>,
  columnId: string,
  filterValue: unknown[]
) => {
  if (!filterValue || filterValue.length === 0) return true;
  const cellValue = row.getValue(columnId);
  const serializedCell = serializeValue(cellValue);
  return filterValue.map(serializeValue).includes(serializedCell);
};

// Define a custom fuzzy filter function that will apply ranking info to rows (using match-sorter utils)
// eslint-disable-next-line max-params
const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value, {
    threshold: 5,
  });

  // const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

// Define a custom fuzzy sort function that will sort by rank if the row has ranking information
const fuzzySort: SortingFn<any> = (rowA, rowB, columnId) => {
  let dir = 0;

  // Only sort by rank if the column has ranking information
  if (rowA.columnFiltersMeta[columnId]) {
    dir = compareItems(
      rowA.columnFiltersMeta[columnId]?.itemRank!,
      rowB.columnFiltersMeta[columnId]?.itemRank!
    );
  }

  // Provide an alphanumeric fallback for when the item ranks are equal
  return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};

// Optimized cell components that avoid unnecessary MobX observers
const IndexCell = memo(({ row }: { row: any }) => <span>{row.index + 1}</span>);

const NodeNumberCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const { intent, settings } = row.original;
  const columnId = column.id;
  const { draftVersionSolutionData } = useProStudioCtxSelector((ctx) => ({
    draftVersionSolutionData: ctx.draftVersionSolutionData,
  }));
  const isTaskNode =
    settings?.taskId &&
    draftVersionSolutionData?.tasks?.some(
      (task) => task.id === settings.taskId
    );

  return (
    <>
      <EditableTableCell
        rowId={rowId}
        columnId={columnId}
        // minWidth="100%"
        // width="100%"
        type="number"
      />
      {intent === 'out_of_scope' && (
        <Tooltip
          test-id="c-editor-table-td-nlu-entry-tooltip"
          content="Natural Language (NL) Entry"
          offset={2}
        >
          <div className="c-editor-table-td__suffix">
            <Icon name="stars" color="purple" />
          </div>
        </Tooltip>
      )}
      {isTaskNode && (
        <Tooltip
          test-id="c-editor-table-td-task-node-tooltip"
          content="Task Node"
        >
          <div className="c-editor-table-td__suffix">
            <Icon name="target" color="purple" />
          </div>
        </Tooltip>
      )}
    </>
  );
});

const NodeTypeCellComponent = memo(
  ({ row, column }: { row: any; column: any }) => {
    const rowId = row.original.id;

    const index = tableStore.getRowIndex(rowId);

    return (
      <NodeTypeCell
        testId={`c-editor-table--nodeType-${index}-select`}
        rowId={row.original.id}
        columnId={column.id}
      />
    );
  }
);

const NodeNameCell = memo(({ row, column }: { row: any; column: any }) => (
  <EditableTableCell rowId={row.original.id} columnId={column.id} />
));

const NextNodesCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const columnId = column.id;

  return (
    <EditableTableCell
      rowId={rowId}
      columnId={columnId}
      onChangeConversion={(input: string) => {
        const stringAsArray = input.split(',').filter(Boolean);

        // if (input && input[input.length - 1] === ',') {
        //   return null;
        // }

        return stringAsArray;
      }}
      renderValue={(arg: string[] | string | number[] | number) => {
        if (typeof arg === 'string') {
          return arg;
        } else if (Array.isArray(arg)) {
          return arg?.join(',') || '';
        } else {
          return '';
        }
      }}
    />
  );
});

const MessageCell = memo(({ row, column }: { row: any; column: any }) => (
  <EditableTableCellTextArea rowId={row.original.id} columnId={column.id} />
));

const UtterancesCell = memo(({ row, column }: { row: any; column: any }) => (
  <EditableTableCellTextArea rowId={row.original.id} columnId={column.id} />
));

const RichAssetTypeCell = memo(({ row, column }: { row: any; column: any }) => (
  <NodeRichAssetSingleSelect
    rowId={row.original.id}
    testId={`c-editor-table--${column.id}-${tableStore.getRowIndex(row.original.id)}-select`}
  />
));

const AnswerRequiredCell = memo(
  ({ row, column }: { row: any; column: any }) => (
    <NodeAnswerRequiredCheckbox
      rowId={row.original.id}
      testId={`c-editor-table--${column.id}-${tableStore.getRowIndex(row.original.id)}-checkbox`}
    />
  )
);

const BehaviorsCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const columnId = column.id;

  return (
    <NodeBehaviorsMultiSelect
      rowId={rowId}
      columnId={columnId}
      onEdit={(values: string[] | undefined) => {
        // When free text is closed (Disable Free Text), NLU is not applicable,
        // so clear NLU Disabled to keep the data honest and avoid an invalid
        // compiler config. See PSTUDIO-2425.
        const freeTextClosed =
          Array.isArray(values) && values.includes('disable_input');

        // Dropdowns should save immediately
        tableStore.updateRow({
          rowId,
          updates: {
            [columnId]: values,
            ...(freeTextClosed ? { [NodeColumns.nluDisabled]: false } : {}),
          },
          skipValidationRules: false,
          saveImmediately: true,
        });
      }}
    />
  );
});

const CommandCell = memo(({ row, column }: { row: any; column: any }) => {
  return (
    <NodeCommandSelect
      rowId={row.original.id}
      testId={`c-editor-table--${column.id}-${tableStore.getRowIndex(row.original.id)}-select`}
    />
  );
});

const OutputCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const columnId = column.id;

  return (
    <EditableTableCell
      rowId={rowId}
      columnId={columnId}
      onChangeConversion={(input: string) => {
        const stringAsArray = input.split(',');

        // if (input && input[input.length - 1] === ',') {
        //   return null;
        // }

        return stringAsArray;
      }}
      renderValue={(arg: string[] | string | number[] | number) => {
        if (typeof arg === 'string') {
          return arg;
        } else if (Array.isArray(arg)) {
          return arg?.join(',') || '';
        } else {
          return '';
        }
      }}
    />
  );
});

const JsonInputCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const columnId = column.id;
  const isDisabled = tableStore.isCellDisabled(rowId, columnId);

  return (
    <EditableTableCellTextArea
      rowId={rowId}
      columnId={columnId}
      index={tableStore.getRowIndex(rowId)}
      disabled={isDisabled}
    />
  );
});

const WhatsNextCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const columnId = column.id;

  return (
    <EditableTableCell
      rowId={rowId}
      columnId={columnId}
      onChangeConversion={(input: string) => {
        const stringAsArray = input.split('|');

        return stringAsArray;
      }}
      renderValue={(arg: string[] | string | number[] | number) => {
        if (typeof arg === 'string') {
          return arg;
        } else if (Array.isArray(arg)) {
          return arg?.join('|') || '';
        } else {
          return '';
        }
      }}
    />
  );
});

const VariablesCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const columnId = column.id;

  return (
    <EditableTableCell
      rowId={rowId}
      columnId={columnId}
      onChangeConversion={(input: string) => {
        const stringAsArray = input.split(',');
        // .filter(Boolean);

        // if (input && input[input.length - 1] === ',') {
        //   return null;
        // }
        return stringAsArray;
      }}
      renderValue={(arg: string[] | string | number[] | number) => {
        if (typeof arg === 'string') {
          return arg;
        } else if (Array.isArray(arg)) {
          return arg?.join(',') || '';
        } else {
          return '';
        }
      }}
    />
  );
});

const ResourceLibControls = memo(
  ({
    isViewer,
    onAddRow,
    onOpenPanel,
  }: {
    isViewer: boolean;
    onAddRow: () => void;
    onOpenPanel: () => void;
  }) => (
    <div className="c-editor-table__controls">
      {!isViewer && (
        <>
          <Tooltip content="Add Node">
            <IconButton
              test-id="ps-editor-table-add-row-button"
              size="small"
              variant="new-primary"
              name="plus"
              onClick={onAddRow}
            />
          </Tooltip>
          <hr className="c-editor-table__controls-divider" />
        </>
      )}
      <Button size="small" variant="ghost" onClick={onOpenPanel}>
        <Icon name="grid-menu" slot="prefix" size="small" />
        Resource Library
      </Button>
    </div>
  )
);

const TableFooter: React.FC<{
  isViewer: boolean;
}> = observer(({ isViewer }) => {
  const { isDebugMode } = useProStudioCtxSelector((ctx) => ({
    isDebugMode: ctx.debugMode,
  }));

  // Search is the only "filtering" surface the footer cares about. Group
  // collapse hides rows but those nodes still exist (and still count in
  // nodeCounts), so it must not surface as "Showing X out of Y".
  const isFiltered = Boolean(tableStore.searchQuery);
  const visibleRowCount = isFiltered
    ? tableStore
        .getVisibleRows()
        .filter((r) => r.nodeType !== Tenancy_Node_Type_Enum_Ps_Enum.G).length
    : 0;

  return (
    <div
      test-id="ps-editor-table-info"
      className="c-editor-table-footer c-editor-table-add-row-controls"
    >
      <div className="c-editor-table-footer__th">
        <div className="c-editor-table-footer__inner">
          {!isViewer && (
            <Stack nowrap alignItems="center" gutter="xsmall">
              <Button
                test-id="ps-editor-table-add-row-button"
                size="small"
                variant="ghost"
                onClick={() => tableStore.addEmptyRow()}
                className="c-editor-table-add-row-control__btn"
              >
                <Icon slot="prefix" name="plus" size="xsmall" />
                Add Node
              </Button>

              <Button
                test-id="ps-editor-table-add-10-rows-button"
                size="small"
                variant="ghost"
                onClick={() => tableStore.addEmptyRows(10)}
              >
                <Icon slot="prefix" name="plus" size="xsmall" />
                Add 10 Nodes
              </Button>

              <Divider vertical />
            </Stack>
          )}

          <Stack
            nowrap
            alignItems="center"
            gutter="small"
            className="u-align-items-center"
          >
            {isDebugMode && !isViewer && (
              <Stack
                nowrap
                alignItems="center"
                gutter="small"
                className="u-align-items-center"
              >
                <Divider vertical />
                <Button
                  test-id="ps-editor-table-reset-order-button"
                  size="small"
                  variant="ghost"
                  onClick={() => tableStore.normalizeRowOrder()}
                  className="c-editor-table-add-row-control__btn"
                >
                  Reset Row Order
                </Button>

                <Button
                  test-id="ps-editor-table-delete-all-nodes-button"
                  size="small"
                  variant="ghost"
                  onClick={() => tableStore.deleteAllRows()}
                  className="c-editor-table-add-row-control__btn"
                >
                  Delete All Rows
                </Button>
              </Stack>
            )}
          </Stack>
          {tableStore.hasPendingRowsToRender && (
            <>
              <Divider vertical />
              <TextBody size="small" variant="primary">
                Loading more nodes...
              </TextBody>
            </>
          )}
          <Stack gutter="xsmall" nowrap className="u-margin-left-auto">
            <TextSubtitle
              test-id={`ps-editor-table-info-total-nodes`}
              size="xsmall"
              variant="secondary"
              truncate
            >
              {isFiltered && `Showing ${visibleRowCount} out of `}
              {tableStore.nodeCounts.totalNodes} Nodes
            </TextSubtitle>
            <Divider vertical />
            <TextBody
              test-id={`ps-editor-table-info-decision-nodes`}
              size="small"
              variant="secondary"
              truncate
            >
              {tableStore.nodeCounts.decisionNodes} Decision Nodes
            </TextBody>
            <TextBody
              test-id={`ps-editor-table-info-action-nodes`}
              size="small"
              variant="secondary"
              truncate
            >
              {tableStore.nodeCounts.actionNodes} Action Nodes
            </TextBody>
          </Stack>
        </div>
      </div>
    </div>
  );
});

/**
 * PSTUDIO-1433: arms the close-on-scroll listener while any row/cell context
 * menu is open.
 *
 * A LEAF observer on purpose. `Table` is the top-level observer, so reading
 * `openContextMenuCount` in its body would subscribe the entire grid: every
 * right-click (0→1) and every close (1→0) would re-render ~1200 cells just to
 * add or remove one listener. Rendering nothing, this component absorbs those
 * re-renders by itself — the same reasoning that keeps `pendingKeyboardPaste`
 * off the observable path (PSTUDIO-3968).
 */
const ContextMenuScrollCloser: React.FC = observer(() => {
  const close = useCallback(() => {
    tableStore.requestCloseAllContextMenus();
  }, []);
  useCloseMenuOnScroll(tableStore.openContextMenuCount > 0, close);
  return null;
});

export const Table: React.FC<{
  projectName?: string;
  botName?: string;
  /**
   * Project Bobcat: a control to host in the middle of the toolbar (the
   * Table/Agent View toggle). Passed straight through to `ControlsBar` — the
   * table itself does not look at it.
   */
  toolbarCenter?: React.ReactNode;
}> = observer(({ toolbarCenter }) => {
  // PSTUDIO-1433: ONE scroll listener for the whole table, replacing a
  // per-cell/per-row one. Scrolling must close any open context menu, because
  // the virtualizer unmounts rows as they leave the window — and unmounting a
  // row whose `ps-dropdown` has projected itself into the popover portal is the
  // `removeChild` crash. Cells and rows react to the store signal this raises.
  // Read the counter inside a LEAF observer, never here: `Table` is the
  // top-level observer, so reading an observable in its body subscribes the
  // whole grid — every right-click (0→1) and every close (1→0) would re-render
  // ~1200 cells to arm one listener. Same reasoning as `pendingKeyboardPaste`
  // being excluded from observability in PSTUDIO-3968.

  const [indexCellWidth, setIndexCellWidth] = useState<number | undefined>(
    undefined
  );
  const handleIndexCellResize = useCallback(
    ({ width }: { width: number | null }) => {
      // Ignore ResizeObserver firings during row-height drag to prevent
      // Table from re-rendering (which would re-render the panel Group, update
      // its context, and cascade re-renders to every Panel).
      if (!resizingRowIdRef.current) {
        // PSTUDIO-3393: round the measured width UP to a whole pixel. The raw
        // ResizeObserver width is sub-pixel (e.g. 40.38); feeding that straight
        // into the TanStack column size lets sub-pixel rounding flip by 1px on
        // repaint during arrow-key navigation (and when the selected row's index
        // turns bold), dragging the whole table 1px. Ceiling to an integer keeps
        // the column width stable across those repaints.
        setIndexCellWidth(width != null ? Math.ceil(width) : undefined);
      }
    },
    []
  );
  const { ref: indexCellRef } = useResizeDetector({
    ...resizeDetectorConfig,
    disableRerender: true,
    onResize: handleIndexCellResize,
  });

  const { isDebugMode, resourceLibraryData } = useProStudioCtxSelector(
    (ctx) => ({
      isDebugMode: ctx.debugMode,
      resourceLibraryData: ctx.resourceLibraryData,
    })
  );

  const isViewer = useProStudioViewerRole();
  // Project Bobcat (Resource Library drag-and-drop): gates the native
  // drag-and-drop wiring below, separate from the @dnd-kit row-reorder system.
  const resourceLibDragDropAccess = useBobcatFlag('fiveStageNav');
  // Own hook instance, independent of ResourceLibPanel's — a dropped item's
  // scripts must upload the same way a clicked one's do (PSTUDIO Bobcat: the
  // drop handler used to skip this entirely, landing nodes whose scripts
  // were never on the solution). Kept in a ref, not the drop effect's own
  // dependency array — `handleUpload` is a new function identity every
  // render, and this effect only needs to (de)register listeners when drag-
  // drop access itself changes.
  const { handleUpload: handleResourceLibUpload } =
    useUploadPythonFiles('scriptFile');
  const handleResourceLibUploadRef = useRef(handleResourceLibUpload);
  handleResourceLibUploadRef.current = handleResourceLibUpload;
  const tableRef = useRef<HTMLTableElement>(null);
  const tableScrollerRef = useRef<HTMLDivElement>(null);
  const cellRefs = useRef(new Map<string, HTMLTableCellElement | null>());
  const rowRefs = useRef(new Map<string, HTMLTableRowElement | null>());
  // Height of the sticky <thead> that overlays the top of the scroller — the
  // reveal (PSTUDIO-3848) subtracts it so a revealed row lands BELOW the header,
  // never tucked under it. Measured from the DOM (below), not a constant.
  const [headerHeight, setHeaderHeight] = useState(0);

  // Row virtualization is set up BEFORE useReactTable so we can window the
  // `data` prop fed to TanStack Table. Without this, useReactTable would
  // build a row model + sort/filter/facet derived models for the full
  // dataset, which is the dominant cost on large workspaces even though the
  // DOM only renders ~50 rows.
  const totalVisibleRowCount = tableStore.getVisibleRows().length;
  const rowVirtualizer = useVirtualizer({
    count: totalVisibleRowCount,
    getScrollElement: () => tableScrollerRef.current,
    estimateSize: () => MIN_ROW_HEIGHT,
    overscan: 25,
    measureElement: virtualizerMeasureElement,
  });
  const virtualizerRange = rowVirtualizer.range;
  // Snap the window boundaries to multiples of SNAP rows so a small scroll
  // doesn't shift the window. With SNAP = 100 + BUFFER = 75, the window
  // shifts only every ~100 rows of scroll. Reading `tableStore.windowedRows`
  // (a MobX computed) returns the SAME array reference until the snapped
  // window or the underlying rows actually change — without this, every
  // scroll tick would produce a fresh slice and useReactTable would rebuild
  // its row model on every frame.
  const VIRTUAL_DATA_BUFFER = 75;
  const VIRTUAL_DATA_SNAP = 100;
  const snappedWindowStart = Math.max(
    0,
    Math.floor(
      ((virtualizerRange?.startIndex ?? 0) - VIRTUAL_DATA_BUFFER) /
        VIRTUAL_DATA_SNAP
    ) * VIRTUAL_DATA_SNAP
  );
  const snappedWindowEnd = Math.min(
    totalVisibleRowCount,
    Math.ceil(
      ((virtualizerRange?.endIndex ?? 0) + VIRTUAL_DATA_BUFFER + 1) /
        VIRTUAL_DATA_SNAP
    ) * VIRTUAL_DATA_SNAP
  );
  // Sync to the store so the `windowedRows` computed re-evaluates only when
  // the snapped boundaries change. The Effect runs after render — first
  // render after a scroll past the snap boundary is one frame stale, but the
  // virtualizer overscan keeps the new viewport rows visible until the
  // window catches up next render.
  useEffect(() => {
    tableStore.setVirtualWindow(snappedWindowStart, snappedWindowEnd);
  }, [snappedWindowStart, snappedWindowEnd]);
  // Hand the virtualizer's scrollToIndex to the store so methods like
  // scrollToAndFocusCell can bring an arbitrary row into view.
  useEffect(() => {
    tableStore.setScrollToVirtualIndex((rowIndex: number) => {
      rowVirtualizer.scrollToIndex(rowIndex, { align: 'center' });
    });
    return () => tableStore.setScrollToVirtualIndex(null);
  }, [rowVirtualizer]);
  // Reveal a freshly inserted/pasted contiguous block (PSTUDIO-3848). The
  // virtualizer's own scrollToIndex NO-OPS on just-added rows (their measurements
  // aren't ready — proven live), so scroll the REAL DOM rows via a pure geometry
  // helper that accounts for the sticky header. An above-insert makes the
  // virtualizer add scroll-compensation AFTER our pass; rather than a watch loop,
  // we re-align whenever scrollTop changes within a short window, and BAIL the
  // instant the user scrolls (never fight intentional input). single-flight +
  // cancelAnimationFrame keep it leak-free across unmount / rapid inserts.
  useEffect(() => {
    let rafId = 0;
    let cancelWatch: (() => void) | null = null;

    const reveal: RevealRange = (
      firstRowId,
      lastRowId,
      firstIndex,
      lastIndex
    ) => {
      cancelWatch?.(); // supersede any in-flight reveal (rapid inserts)
      const sc = tableScrollerRef.current;
      const rowEl = (id: string) =>
        rowRefs.current.get(id) ??
        sc?.querySelector<HTMLElement>(`tr[data-rowid="${id}"]`) ??
        null;

      // One alignment pass. Returns false only when the FIRST row isn't in the
      // DOM (block virtualized far out of the window) so the caller can fall back.
      const alignOnce = (): boolean => {
        if (!sc) return false;
        const firstEl = rowEl(firstRowId);
        if (!firstEl) return false;
        const lastEl = rowEl(lastRowId) ?? firstEl; // last virtualized → degrade
        const scRect = sc.getBoundingClientRect();
        const delta = computeRevealScrollDelta({
          viewTop: scRect.top + headerHeight,
          viewBottom: scRect.top + sc.clientHeight, // excludes any h-scrollbar
          firstTop: firstEl.getBoundingClientRect().top,
          lastBottom: lastEl.getBoundingClientRect().bottom,
        });
        if (delta !== 0) sc.scrollTop += delta;
        return true;
      };

      if (!alignOnce()) {
        // Rows outside the rendered window — coarse virtualizer scroll (best
        // effort; measurements make this imperfect, but such a block is rare).
        rowVirtualizer.scrollToIndex(lastIndex, { align: 'auto' });
        rowVirtualizer.scrollToIndex(firstIndex, { align: 'auto' });
        return;
      }

      // Watch for the virtualizer's post-insert scroll-compensation (only an
      // above-insert triggers it) and re-align once it settles; cancel on any
      // user scroll so we don't hijack the viewport.
      if (!sc) return;
      let prev = sc.scrollTop;
      let moved = false;
      let quiet = 0;
      let frames = 0;
      let userScrolled = false;
      const onUser = () => {
        userScrolled = true;
      };
      sc.addEventListener('wheel', onUser, { passive: true });
      sc.addEventListener('touchstart', onUser, { passive: true });
      const stop = () => {
        cancelAnimationFrame(rafId);
        sc.removeEventListener('wheel', onUser);
        sc.removeEventListener('touchstart', onUser);
        cancelWatch = null;
      };
      cancelWatch = stop;
      const watch = () => {
        if (userScrolled || !sc.isConnected) return stop();
        const cur = sc.scrollTop;
        if (cur !== prev) {
          moved = true;
          quiet = 0;
        } else {
          quiet += 1;
        }
        prev = cur;
        frames += 1;
        if (moved && quiet >= 3) {
          alignOnce();
          return stop();
        }
        if (frames >= 40) {
          if (moved) alignOnce();
          return stop();
        }
        rafId = requestAnimationFrame(watch);
      };
      rafId = requestAnimationFrame(watch);
    };

    tableStore.setScrollRangeIntoView(reveal);
    return () => {
      cancelWatch?.();
      cancelAnimationFrame(rafId);
      tableStore.setScrollRangeIntoView(null);
    };
  }, [rowVirtualizer, headerHeight]);
  // Keep `headerHeight` (→ scrollPaddingStart) in sync with the sticky <thead>.
  // Measured from the DOM (not a constant) so a wrapped/taller header still lines
  // reveals up correctly; a ResizeObserver tracks any later height change.
  useLayoutEffect(() => {
    const thead = tableScrollerRef.current?.querySelector('thead');
    if (!thead) return undefined;
    const measure = () => {
      const h = thead.getBoundingClientRect().height;
      setHeaderHeight((prev) => (prev === h ? prev : h));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(thead);
    return () => ro.disconnect();
  }, []);
  const { windowedRows } = tableStore;

  // Pre-allocate index column min-width to fit the largest row number, so
  // virtualized rows revealing more digits (row 9 -> 10, 99 -> 100, ...)
  // don't widen the column and shift the rest of the table.
  useEffect(() => {
    if (!tableRef.current) return;
    const digits = Math.max(2, String(totalVisibleRowCount).length);
    // ~7px per digit, centered with a small buffer; clamp to 40px so
    // 1-2 digit workspaces stay close to the previous auto-sized width.
    const minWidth = Math.max(40, 26 + digits * 7);
    tableRef.current.style.setProperty(
      '--index-col-min-width',
      `${minWidth}px`
    );
  }, [totalVisibleRowCount]);

  const [supervisorPanelOpen, showSupervisorPanel] = useState(false);
  // The toolbar panel holds only the controls bar, so it is pinned to a fixed
  // height via minSize === maxSize (PSTUDIO-3910 removed the drag handle that
  // used to let it grow — dragging only added empty space under the toolbar).
  const toolbarSize = '41px';
  const sidebarSize = '500px';
  // In the store, not local state: the Build stage's canvas opens the same
  // panel from its own entry point.
  const { resourceLibPanelOpen } = tableStore;
  const showResourceLibPanel = useCallback(
    (open: boolean | ((current: boolean) => boolean)) =>
      tableStore.setResourceLibPanelOpen(
        typeof open === 'function'
          ? open(tableStore.resourceLibPanelOpen)
          : open
      ),
    []
  );
  // Right-side panels close when you move to another screen; the table
  // leaving (a stage or Build view change) is that move.
  useEffect(() => () => tableStore.setResourceLibPanelOpen(false), []);

  // PSTUDIO-3624: the sidebar hosts one panel at a time. This closes the
  // overlay side panels (Supervisor / ResourceLib); threaded into ControlsBar
  // so Task 4's Expanded Cell View toggle can enforce mutual exclusivity.
  const closeOverlaySidePanels = useCallback(() => {
    showSupervisorPanel(false);
    showResourceLibPanel(false);
  }, [showResourceLibPanel]);

  // PSTUDIO-3624 / Project Bobcat: enforce "one sidebar panel at a time" from
  // the STORE side too, so an opener that only reaches the store (Bobcat's
  // click-to-open, in cell.tsx) still closes the React-state panels. Logic and
  // rationale live in the module so they are testable without rendering this
  // component.
  useEffect(() => {
    const disposeReaction = wireOneSidebarPanelAtATime(closeOverlaySidePanels);
    return () => {
      disposeReaction();
      // A cell click schedules the open 250ms out (the double-click grace). If
      // the table goes away inside that window — switching to Agent View,
      // leaving the builder — the timer would still fire against the store
      // singleton and flip the PERSISTED `expandedCellViewOpen`, so the panel
      // came back open on a later visit for a click the user had abandoned.
      // The reaction that keeps one sidebar at a time is disposed by then too,
      // so nothing would tidy up after it.
      tableStore.cancelPendingPanelOpen();
    };
  }, [closeOverlaySidePanels]);

  // PSTUDIO-1433: auto-open the Expanded Cell View panel when the user enters
  // INLINE edit on a JSON-capable cell (richAssetContent / parameterInput; not
  // analyticsEvent since PSTUDIO-4784 — its cell is a select, which never sets
  // editingColumnId) so the JSON CodeEditor is available without a separate
  // gesture. editingColumnId is set ONLY by toggleCellInput (a real in-grid text
  // edit), never by focusing the panel's own editor, so it is a clean edit-entry
  // signal that covers every entry gesture (double-click, Enter, type-to-edit /
  // F2). The decision + the already-open guard live in the pure, unit-tested
  // shouldAutoOpenExpandedCellView helper. closeOverlaySidePanels lives in this
  // component's React state (unreachable from a store hotkey), so the reaction
  // runs here and mirrors the Shift+Enter / toolbar open paths that also close
  // the overlay panels before opening (one-panel-at-a-time). The reaction is
  // disposed on unmount (perf rule: every subscription has a matching cleanup).
  useEffect(() => {
    const dispose = reaction(
      () => tableStore.editingColumnId,
      (editingColumnId) => {
        if (
          shouldAutoOpenExpandedCellView(
            editingColumnId ?? undefined,
            tableStore.expandedCellViewOpen
          )
        ) {
          closeOverlaySidePanels();
          tableStore.setExpandedCellViewOpen(true);
        }
        // Move focus INTO the panel's editor on every auto-open-worthy edit
        // entry (even when the panel was already open — switching to another
        // JSON cell should refocus the panel). Gate only on the column being
        // JSON-capable; the panel itself performs the focus (it owns the
        // CodeEditor DOM) via this nonce.
        if (editingColumnId && isJsonCapableColumn(editingColumnId)) {
          tableStore.requestExpandedCellViewAutofocus();
        }
      }
    );
    return dispose;
  }, [closeOverlaySidePanels]);

  // PSTUDIO-1433: persist the Expanded Cell View panel width as the user drags
  // the Separator. onResize fires on every drag tick; writing the observable
  // width (and localStorage) per tick would re-render this top-level observer
  // every frame and thrash localStorage, so debounce the drag stream. The Panel
  // is uncontrolled (defaultSize only seeds it), so debouncing does not fight
  // the live drag. Cancelled on unmount (perf rule: every timer has a cleanup).
  const debouncedPersistExpandedCellViewWidth = useMemo(
    () =>
      debounce((px: number) => tableStore.setExpandedCellViewWidth(px), 200),
    []
  );
  useEffect(
    () => () => debouncedPersistExpandedCellViewWidth.cancel(),
    [debouncedPersistExpandedCellViewWidth]
  );

  const handleAddRow = useCallback(() => tableStore.addEmptyRow(), []);
  const handleOpenResourceLib = useCallback(() => {
    // Opening ResourceLib closes Expanded Cell View + the keyboard-shortcuts
    // panel (PSTUDIO-3624 / PSTUDIO-3623).
    tableStore.setExpandedCellViewOpen(false);
    tableStore.setKeyboardShortcutsOpen(false);
    showResourceLibPanel((state) => !state);
  }, [showResourceLibPanel]);

  // Row resize state management — refs only, no React state (avoids re-rendering all rows)
  const resizingRowIdRef = useRef<string | null>(null);
  const resizeRowInitialTopRef = useRef<number | null>(null);
  const resizeHeightRef = useRef<number | null>(null);
  const MIN_ROW_HEIGHT = 32;

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // PSTUDIO-2154: sort state lives in tableStore (not local React state) so
  // undo/redo commands can drive it. This component is a MobX observer, so
  // reading tableStore.sortingState keeps it reactive.
  const sorting = tableStore.sortingState;
  const { listeners, tableNav } = useTableNav({ debug: true });

  // Read so observer re-renders when set from ValidationPopover (navigate from other page)
  const {
    pendingScrollToCell,
    pendingScrollToCellByRowId,
    pendingGoToNodeNumber,
  } = tableStore;

  useEffect(() => {
    tableStore.setScrollToCellCallback((el) => tableNav.scrollToCell(el));
    return () => tableStore.setScrollToCellCallback(null);
  }, [tableNav]);

  useEffect(() => {
    const pending = pendingScrollToCell;
    if (!pending) return;
    tableStore.scrollToAndFocusCell(pending.rowIndex, pending.columnId);
    tableStore.clearPendingScrollToCell();
  }, [pendingScrollToCell]);

  useEffect(() => {
    const pending = pendingScrollToCellByRowId;
    if (!pending) return;
    tableStore.scrollToAndFocusCellById(pending.rowId, pending.columnId);
    tableStore.clearPendingScrollToCellByRowId();
  }, [pendingScrollToCellByRowId]);

  useEffect(() => {
    const pending = pendingGoToNodeNumber;
    if (!pending) return;

    const scroller = tableScrollerRef.current;
    if (!scroller) return;

    const goToNode = () => {
      tableStore.goToNode(pending.nodeNumber);
      tableStore.clearPendingGoToNodeNumber();
    };

    if (scroller.clientHeight > 0) {
      const rafId = requestAnimationFrame(goToNode);
      return () => cancelAnimationFrame(rafId);
    }

    const sizeObserver = new ResizeObserver(() => {
      if (scroller.clientHeight > 0) {
        sizeObserver.disconnect();
        goToNode();
      }
    });
    sizeObserver.observe(scroller);
    return () => sizeObserver.disconnect();
  }, [pendingGoToNodeNumber]);

  useEffect(() => {
    // todo: refactor out to use a CSS classname based on the route when React Router gets upgraded
    document.documentElement.style.overscrollBehaviorX = 'contain';
    document.body.style.overscrollBehaviorX = 'contain';

    return () => {
      document.documentElement.style.removeProperty('overscroll-behavior-x');
      document.body.style.removeProperty('overscroll-behavior-x');
    };
  }, []);

  useEffect(() => {
    // todo: refactor out to use a CSS classname based on the route when React Router gets upgraded
    const tableBodyHandler = (event: MouseEvent) => {
      if (!tableRef.current) return;

      if (event.target instanceof Element) {
        if (
          tableRef.current &&
          event.target?.contains(
            tableRef.current?.querySelector('.c-editor-table-td--index-cell')
          )
        ) {
          return;
        }

        if (
          event.target?.contains(tableRef.current) ||
          event.target?.closest('ps-universal-nav') ||
          event.target?.closest('.c-editor-table-info')
        ) {
          tableStore.deselectAllCells();
          tableStore.clearSelectedStartCell();
          tableStore.deselectAllRows();
        }
      }
    };
    document.body.addEventListener('click', tableBodyHandler);

    return () => {
      document.body.removeEventListener('click', tableBodyHandler);
    };
  }, []);

  // Centralized row resize listeners - stable callbacks, attached on resize start
  const handleResizeMouseMove = useCallback((e: MouseEvent) => {
    const newHeight = Math.max(
      MIN_ROW_HEIGHT,
      e.clientY - resizeRowInitialTopRef.current!
    );
    resizeHeightRef.current = newHeight;
    // Directly update the DOM — no React state change, no re-renders
    const rowEl = rowRefs.current.get(resizingRowIdRef.current!);
    if (rowEl)
      rowEl.style.setProperty('--c-editor-table-tr-height', `${newHeight}px`);
  }, []);

  const handleResizeMouseUp = useCallback(() => {
    const resizingRowId = resizingRowIdRef.current;
    if (resizingRowId && resizeHeightRef.current !== null) {
      const { selectedRows } = tableStore;
      if (selectedRows.size > 1 && selectedRows.has(resizingRowId)) {
        [...selectedRows.values()].map((value) => {
          tableStore.updateRow({
            rowId: value,
            updates: {
              settings: {
                ...tableStore.getValue(value, NodeColumns.settings),
                rowHeight: resizeHeightRef.current,
              },
            },
            skipValidationRules: true,
            saveImmediately: true,
          });
        });
      } else {
        tableStore.updateRow({
          rowId: resizingRowId,
          updates: {
            settings: {
              ...tableStore.getValue(resizingRowId, NodeColumns.settings),
              rowHeight: resizeHeightRef.current,
            },
          },
          skipValidationRules: true,
          saveImmediately: true,
        });
      }
    }
    resizingRowIdRef.current = null;
    tableStore.setResizingRowId(null);
    resizeRowInitialTopRef.current = null;
    resizeHeightRef.current = null;
    document.removeEventListener('mousemove', handleResizeMouseMove);
    document.removeEventListener('mouseup', handleResizeMouseUp);
  }, [handleResizeMouseMove]);

  // useEffect(() => {
  //   if (
  //     tableStore.currentState !== 'loading' &&
  //     tableStore.hasPendingRowsToRender
  //   ) {
  //     requestIdleCallback(() => {
  //       tableStore.loadPendingRows();
  //     });
  //   }
  // }, [hasPendingRowsToRender]);

  // Warn user about unsaved changes before leaving the page — including a node
  // write that has not reached the database yet (PSTUDIO-4342, hasUnsavedWork).
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (tableStore.hasUnsavedWork) {
        // Modern browsers require returnValue to be set
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // For older browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const columnSizes = tableStore.columnsSizes;
  const columns = useMemo(
    () =>
      [
        // 134mb
        {
          id: 'index',
          header: 'Index',
          cell: IndexCell,
          size: indexCellWidth && indexCellWidth >= 42 ? indexCellWidth : 42,
          minSize: indexCellWidth && indexCellWidth >= 42 ? indexCellWidth : 42,
          maxSize: indexCellWidth && indexCellWidth >= 42 ? indexCellWidth : 42,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // {
        //   accessorKey: 'pinned',
        //   id: 'pinned',
        //   header: 'Pinned',
        //   cell: ({ row }: { row: Row<TableRow> }) => (
        //     <button
        //       onClick={() => {
        //         if (row.getIsPinned()) {
        //           runInAction(() => {
        //             tableStore.getRowById(row.original.id).pinned = false;
        //           });
        //           row.pin(false);
        //         } else {
        //           runInAction(() => {
        //             tableStore.getRowById(row.original.id).pinned = true;
        //           });
        //           row.pin('top');
        //         }
        //       }}
        //     >
        //       {row.getIsPinned() ? '❌' : '📌'}
        //     </button>
        //   ),
        //   minSize: 84,
        //   maxSize: 84,
        //   // hidden: !isDebugMode,
        //   // enableColumnFilter: true,
        //   // filterFn: multiValueFilter,
        // },
        {
          accessorKey: 'order',
          id: 'order',
          header: nodeColumnDisplayNames[NodeColumns.order],
          cell: ({ row }: { row: Row<TableRow> }) => (
            <span>{row.original.order}</span>
          ),
          minSize: 84,
          maxSize: 84,
          // Debug column to show order values - only in development
          // hidden: !isDebugMode,
          hidden: true,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 220mb
        {
          accessorKey: NodeColumns.nodeNumber,
          id: NodeColumns.nodeNumber,
          header: nodeColumnDisplayNames[NodeColumns.nodeNumber],
          maxSize: 200,
          cell: NodeNumberCell,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 230mb
        {
          accessorKey: NodeColumns.nodeType,
          id: NodeColumns.nodeType,
          header: nodeColumnDisplayNames[NodeColumns.nodeType],
          minSize: 74,
          cell: NodeTypeCellComponent,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 225mb
        {
          accessorKey: NodeColumns.nodeName,
          id: NodeColumns.nodeName,
          header: nodeColumnDisplayNames[NodeColumns.nodeName],
          cell: NodeNameCell,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
          enableSorting: true,
          sortingFn: fuzzySort,
        },
        {
          accessorKey: NodeColumns.utterances,
          id: NodeColumns.utterances,
          header: nodeColumnDisplayNames[NodeColumns.utterances],
          cell: UtterancesCell,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 327mb
        {
          accessorKey: NodeColumns.message,
          id: NodeColumns.message,
          header: nodeColumnDisplayNames[NodeColumns.message],
          cell: MessageCell,
          minSize: 160,
          maxSize: 700,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 376mb
        // {
        //   accessorKey: 'embed',
        //   header: 'Embed',
        //   cell: (cell) => (
        //     <EmbedsCell
        //       index={cell.row.index + 1}
        //       onShow={handleEmbedHeaderClick}
        //       onAddEmbed={() => showEmbedsPanel(true)}
        //     />
        //   ),
        //   size: 424,
        // },
        {
          accessorKey: NodeColumns.richAssetType,
          id: NodeColumns.richAssetType,
          header: nodeColumnDisplayNames[NodeColumns.richAssetType],
          cell: RichAssetTypeCell,
          minSize: 148,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 415mb
        {
          accessorKey: NodeColumns.richAssetContent,
          id: NodeColumns.richAssetContent,
          header: nodeColumnDisplayNames[NodeColumns.richAssetContent],
          cell: RichAssetContentCell,
          maxSize: 600,
          minSize: 140,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 450mb
        {
          accessorKey: NodeColumns.behaviors,
          id: NodeColumns.behaviors,
          header: nodeColumnDisplayNames[NodeColumns.behaviors],
          cell: BehaviorsCell,
          minSize: 235,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 431mb
        {
          accessorKey: NodeColumns.answerRequired,
          id: NodeColumns.answerRequired,
          header: nodeColumnDisplayNames[NodeColumns.answerRequired],
          cell: AnswerRequiredCell,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 257mb
        {
          accessorKey: NodeColumns.nluDisabled,
          id: NodeColumns.nluDisabled,
          header: nodeColumnDisplayNames[NodeColumns.nluDisabled],
          // Cell body renders via NativeCellValue's CHECKBOX_COLUMNS branch
          // (the perf refactor moved cell rendering off the column-def `cell`
          // renderer); the nluDisabled tooltip lives there now. PSTUDIO-2425.
          maxSize: 200,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 280mb
        {
          accessorKey: NodeColumns.nextNodes,
          id: NodeColumns.nextNodes,
          header: nodeColumnDisplayNames[NodeColumns.nextNodes],
          cell: NextNodesCell,
          maxSize: 200,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        {
          accessorKey: NodeColumns.cssClassname,
          id: NodeColumns.cssClassname,
          header: nodeColumnDisplayNames[NodeColumns.cssClassname],
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 458mb
        {
          accessorKey: NodeColumns.command,
          id: NodeColumns.command,
          header: nodeColumnDisplayNames[NodeColumns.command],
          cell: CommandCell,
          minSize: 130,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 489mb
        {
          accessorKey: NodeColumns.description,
          id: NodeColumns.description,
          header: nodeColumnDisplayNames[NodeColumns.description],
          minSize: 130,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 498mb -- possible memory leak starts
        {
          accessorKey: NodeColumns.output,
          id: NodeColumns.output,
          header: nodeColumnDisplayNames[NodeColumns.output],
          cell: OutputCell,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 515mb
        {
          accessorKey: NodeColumns.nodeInput,
          id: NodeColumns.nodeInput,
          header: nodeColumnDisplayNames[NodeColumns.nodeInput],
          minSize: 93,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 530mb
        {
          accessorKey: NodeColumns.parameterInput,
          id: NodeColumns.parameterInput,
          header: nodeColumnDisplayNames[NodeColumns.parameterInput],
          cell: JsonInputCell,
          minSize: 280,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 550mb
        {
          accessorKey: NodeColumns.decisionVariable,
          id: NodeColumns.decisionVariable,
          header: nodeColumnDisplayNames[NodeColumns.decisionVariable],
          minSize: 127,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 570mb -- possible memory leak end
        {
          accessorKey: NodeColumns.whatsNext,
          id: NodeColumns.whatsNext,
          header: nodeColumnDisplayNames[NodeColumns.whatsNext],
          cell: WhatsNextCell,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 635mb
        {
          accessorKey: NodeColumns.variables,
          id: NodeColumns.variables,
          header: nodeColumnDisplayNames[NodeColumns.variables],
          cell: VariablesCell,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 615 mb
        {
          accessorKey: NodeColumns.skillTag,
          id: NodeColumns.skillTag,
          header: nodeColumnDisplayNames[NodeColumns.skillTag],
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        // 653mb
        {
          accessorKey: NodeColumns.analyticsEvent,
          id: NodeColumns.analyticsEvent,
          header: nodeColumnDisplayNames[NodeColumns.analyticsEvent],
          cell: JsonInputCell,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        {
          accessorKey: NodeColumns.nodeTags,
          id: NodeColumns.nodeTags,
          header: nodeColumnDisplayNames[NodeColumns.nodeTags],
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
        {
          accessorKey: NodeColumns.platformFlag,
          id: NodeColumns.platformFlag,
          header: nodeColumnDisplayNames[NodeColumns.platformFlag],
          minSize: 104,
          enableColumnFilter: true,
          filterFn: multiValueFilter,
        },
      ]
        .map((col) => ({
          ...col,
          size: columnSizes?.[col.id as keyof ColumnsWidths] || col.size,
          minSize: col.minSize || columnDefaultSizes.minWidth,
          maxSize: col.maxSize || columnDefaultSizes.maxWidth,

          // if (col.id === 'index') {
          //   return col;
          // }

          // return {
          //   ...col,
          //   size:
          //     tableStore.columnsSizes?.[col.id as keyof ColumnsWidths] || col.size,
          //   minSize: col.minSize || columnDefaultSizes.minWidth,
          //   maxSize: col.maxSize || columnDefaultSizes.maxWidth,
          // };
        }))
        // PSTUDIO-1345: drop hidden columns from the table instance entirely so no
        // header/body/getAllColumns/getVisibleCells/colSpan surface renders them.
        .filter((col) => !HIDDEN_COLUMNS.has(col.id as NodeColumns)),
    // isDebugMode is read inside the column definitions (Order column's
    // `hidden` flag); the lint rule misses this because the read happens
    // inside an inline column descriptor literal, not at memo callback
    // root scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnSizes, indexCellWidth, isDebugMode]
  );

  const [globalFilter, setGlobalFilter] = useState('');

  // Define the custom filter function
  const fuseFilterFn: FilterFn<any> = (
    row: Row<any>,
    _columnId: string,
    filterValue: any
  ) => {
    const searchPattern = String(filterValue ?? '').trim();
    if (searchPattern === '') return true;
    return tableStore.searchResultIds.has(row.original.id);
  };

  const defaultColumn = useMemo(
    () => ({
      cell: NodeNameCell,
      size: 150,
      minSize: 50,
      maxSize: 500,
    }),
    []
  );

  const filterFns = useMemo(
    () => ({
      multiValue: multiValueFilter,
      fuzzy: fuzzyFilter,
      fuseFilter: fuseFilterFn,
    }),
    []
  );

  const handleColumnPinningChange = useCallback(
    (updater: Parameters<typeof tableStore.setColumnPinningSettings>[0]) =>
      tableStore.setColumnPinningSettings(updater),
    []
  );

  // PSTUDIO-2154: route TanStack's sort change through the store so it's
  // recorded as an undo/redo command (and survives via tableStore.sortingState).
  const handleSortingChange = useCallback(
    (updater: Parameters<typeof tableStore.setSorting>[0]) =>
      tableStore.setSorting(updater),
    []
  );

  const table = useReactTable<TableRow>({
    data: windowedRows,
    columns,
    defaultColumn,
    state: {
      columnPinning: tableStore.columnPinningSettings,
      columnFilters,
      sorting,
      globalFilter,
    },
    // manualPagination: true,
    autoResetPageIndex: false,
    enableRowSelection: true,
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: fuseFilterFn,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    // Manual pagination so TanStack Table doesn't try to slice the windowed
    // data we already pre-windowed for it.
    manualPagination: true,
    pageCount: 1,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getSortedRowModel: getSortedRowModel(),
    onColumnPinningChange: handleColumnPinningChange,
    columnResizeMode: 'onChange',
    filterFns,
  });

  useEffect(() => {
    if (table) {
      tableStore.table = table;
    }

    if (cellRefs.current) {
      tableStore.cellRefs = cellRefs;
    }

    if (rowRefs.current) {
      tableStore.rowRefs = rowRefs;
    }

    return () => {
      if (tableStore.dragSession) {
        tableStore.endDragSession();
      }
      if (tableStore.rowRangeSelection) {
        tableStore.endRowRangeSelection();
      }
    };
  }, [table, cellRefs, rowRefs]);

  // Project Bobcat (Resource Library drag-and-drop): dropping a Resource
  // Library card onto a row adds it there instead of always appending at the
  // end. Deliberately native HTML5 drag-and-drop, NOT the @dnd-kit
  // DragDropProvider below — that provider is scoped to just the <tbody>'s
  // sortable rows for row-reordering; wiring a second drag SOURCE (the side
  // panel, which renders outside the provider) into it would mean moving the
  // provider to wrap the panel too, risking the existing reorder / undo /
  // multi-row-drag behavior. This is a fully separate, independent drag
  // system — it only toggles a highlight class and calls into the store, so
  // it cannot interfere with @dnd-kit's drag session.
  useEffect(() => {
    if (resourceLibDragDropAccess !== 'allowed') return undefined;

    const scroller = tableScrollerRef.current;
    if (!scroller) return undefined;

    let hoveredRowId: string | null = null;

    const setHovered = (rowId: string | null) => {
      if (hoveredRowId === rowId) return;
      if (hoveredRowId) {
        rowRefs.current
          .get(hoveredRowId)
          ?.classList.remove('c-editor-table-tr--resource-drop-target');
      }
      if (rowId) {
        rowRefs.current
          .get(rowId)
          ?.classList.add('c-editor-table-tr--resource-drop-target');
      }
      hoveredRowId = rowId;
    };

    const rowIdFromEvent = (e: DragEvent): string | null =>
      (e.target as HTMLElement | null)
        ?.closest<HTMLElement>('tr[data-rowid]')
        ?.getAttribute('data-rowid') ?? null;

    const handleResourceDragOver = (e: DragEvent) => {
      if (!tableStore.resourceLibDragData) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      setHovered(rowIdFromEvent(e));
    };

    const handleResourceDragLeave = (e: DragEvent) => {
      // Only clear when leaving the scroller entirely — moving between rows
      // inside it is handled by the next row's own dragover.
      if (!scroller.contains(e.relatedTarget as Node | null)) {
        setHovered(null);
      }
    };

    const handleResourceDrop = (e: DragEvent) => {
      const drag = tableStore.resourceLibDragData;
      if (!drag) return;
      e.preventDefault();
      const targetRowId = rowIdFromEvent(e);
      setHovered(null);
      tableStore.setResourceLibDragData(null);
      if (!targetRowId) return;

      // Same script-upload step the click-to-add path runs — without it, a
      // dropped resource's rows reference commands whose .py files were
      // never uploaded, and validation immediately flags them as missing.
      void (async () => {
        try {
          await uploadResourceLibScripts(
            scriptNamesFor(drag.data),
            handleResourceLibUploadRef.current
          );
          tableStore.addResourceLibRowsAtRow(drag.data, targetRowId, drag.name);
        } catch (err) {
          console.error(err);
          sendToast({
            type: 'error',
            text: 'Failed to add Resource Library',
            autoClose: 3000,
          });
        }
      })();
    };

    scroller.addEventListener('dragover', handleResourceDragOver);
    scroller.addEventListener('dragleave', handleResourceDragLeave);
    scroller.addEventListener('drop', handleResourceDrop);

    return () => {
      scroller.removeEventListener('dragover', handleResourceDragOver);
      scroller.removeEventListener('dragleave', handleResourceDragLeave);
      scroller.removeEventListener('drop', handleResourceDrop);
      setHovered(null);
    };
  }, [resourceLibDragDropAccess]);

  // Every valid drop target should be visible for the whole drag, not just
  // the one row you happen to be over — matches the design (Protolab's
  // `body.is-dragging-resource` outline on every group). `dragstart` fires
  // in the Resource Library panel, a different component tree than this
  // table, so the table can't learn about it from its own DOM events; it
  // observes `resourceLibDragData` instead, the same MobX field the drop
  // handler above already reads.
  useEffect(() => {
    const scrollerAtSetup = tableScrollerRef.current;
    const disposer = reaction(
      () => tableStore.resourceLibDragData !== null,
      (isDragging) => {
        tableScrollerRef.current?.classList.toggle(
          'is-dragging-resource',
          isDragging
        );
      }
    );
    return () => {
      disposer();
      scrollerAtSetup?.classList.remove('is-dragging-resource');
    };
  }, []);

  const handleDragEnd: DragDropEvents['dragend'] = (event) => {
    const { target } = event.operation;
    // Derive sourceRowId from BOTH the store-side dragSession AND the
    // dnd-kit event. The session may be torn down or never populated for
    // group rows in the alpha API, but the event always carries the
    // sortable id of the actually-dragged element — use that as the
    // canonical source of truth.
    const sourceFromEvent = (
      event.operation.source as { sortable?: { id?: string } }
    )?.sortable?.id;
    const sourceRowId = tableStore.dragSession?.activeRowId ?? sourceFromEvent;
    const selectedRowIds = tableStore.dragSession?.selectedRowIds ?? [];
    const isMultiRowDrag =
      selectedRowIds.length > 1 && selectedRowIds.includes(sourceRowId || '');

    // PSTUDIO-2154: snapshot every row's order before the drag mutates them so
    // the reorder can be registered as an undo/redo command afterwards. Captured
    // here (not in the store) because the drag algorithm — including the
    // group-aware paths — lives in this handler.
    const reorderBefore = new Map<string, number>(
      tableStore.rows.map((r): [string, number] => [r.id, r.order])
    );

    emitOnce(() => {
      if (isMultiRowDrag) {
        const insertionIndex = tableStore.dragSession?.insertionIndex;

        if (
          insertionIndex === null ||
          insertionIndex === undefined ||
          !sourceRowId
        ) {
          tableStore.endDragSession();

          return;
        }

        const sortedRows = tableStore
          .getAllSortedRows()
          .filter((row) => row.hidden !== true);

        const selectedIndices = selectedRowIds
          .map((id) => sortedRows.findIndex((row) => row.id === id))
          .filter((idx) => idx !== -1)
          .sort((a, b) => a - b);

        const rowsToMove = selectedIndices.map((idx) => sortedRows[idx]);
        const selectedIdSet = new Set(selectedRowIds);
        const remainingRows = sortedRows.filter(
          (row) => !selectedIdSet.has(row.id)
        );

        let adjustedInsertIndex = insertionIndex;
        for (const idx of selectedIndices) {
          if (idx < insertionIndex) {
            adjustedInsertIndex--;
          }
        }
        adjustedInsertIndex = Math.max(
          0,
          Math.min(adjustedInsertIndex, remainingRows.length)
        );

        // PSTUDIO-2406: fractional indexing for multi-row reorder.
        // Same idea as the single-row path below, but distributing K orders
        // evenly across the gap between the insertion neighbours. The moved
        // K rows are the only ones whose order changes; everyone else keeps
        // their existing (possibly drifted) order. Falls back to a clean
        // full-grid renumber if the gap is too narrow to fit K integer
        // orders.
        const leftNeighbor =
          adjustedInsertIndex > 0
            ? remainingRows[adjustedInsertIndex - 1]
            : undefined;
        const rightNeighbor =
          adjustedInsertIndex < remainingRows.length
            ? remainingRows[adjustedInsertIndex]
            : undefined;
        const leftOrder = leftNeighbor?.order ?? null;
        const rightOrder = rightNeighbor?.order ?? null;
        const increment = tableStore.orderIncrementV2;
        const k = rowsToMove.length;

        let proposedOrders: number[] | null = null;
        if (leftOrder !== null && rightOrder !== null) {
          // Need at least k+1 integer steps in the gap to seat k rows
          // between the neighbours without colliding with either.
          if (rightOrder - leftOrder >= k + 1) {
            const gap = (rightOrder - leftOrder) / (k + 1);
            proposedOrders = rowsToMove.map((_, i) =>
              Math.floor(leftOrder + gap * (i + 1))
            );
          }
        } else if (leftOrder !== null) {
          // Dropped at the end: leftOrder + 1*increment, +2*increment, ...
          proposedOrders = rowsToMove.map(
            (_, i) => leftOrder + increment * (i + 1)
          );
        } else if (rightOrder !== null) {
          // Dropped at the start: leave a gap above each so we land cleanly
          // before the right neighbour. Need rightOrder > k for integer math.
          if (rightOrder > k) {
            const gap = rightOrder / (k + 1);
            proposedOrders = rowsToMove.map((_, i) =>
              Math.floor(gap * (i + 1))
            );
          }
        } else {
          // Empty workflow: 1*increment, 2*increment, ...
          proposedOrders = rowsToMove.map((_, i) => increment * (i + 1));
        }

        // Validate that the proposed orders don't collide with each other
        // or with the neighbours (could happen on integer rounding).
        const proposedValid =
          proposedOrders !== null &&
          new Set(proposedOrders).size === proposedOrders.length &&
          proposedOrders.every((o) => o !== leftOrder && o !== rightOrder);

        if (proposedValid && proposedOrders !== null) {
          // Fast path: only the K moved rows' orders change.
          const newOrders = new Map(
            rowsToMove.map((row, i) => [row.id, proposedOrders![i]])
          );
          tableStore.reorderRows(newOrders);
        } else {
          // Slow path: gap exhausted (or rounding collision). Re-number
          // every row on a clean grid.
          const fullResult = [
            ...remainingRows.slice(0, adjustedInsertIndex),
            ...rowsToMove,
            ...remainingRows.slice(adjustedInsertIndex),
          ];
          const newOrderById = new Map(
            fullResult.map((row, i) => [row.id, increment * (i + 1)])
          );
          tableStore.reorderRows(newOrderById);
        }

        // PSTUDIO-2406 spike: O(n) duplicate-order check, no toJS deep clone.
        // Was [...toJS(sortedRows)].some((e,i,arr) => arr.indexOf(e) !== i)
        // — toJS deep-clones every row (50–100 MB on 5000 rows) and indexOf
        // is O(n²). Set-based scan is O(n) with no per-row allocation.
        const seenOrders = new Set<number>();
        let hasDuplicates = false;
        for (const row of tableStore.sortedRows) {
          const orderKey = row.order ?? 0;
          if (seenOrders.has(orderKey)) {
            hasDuplicates = true;
            break;
          }
          seenOrders.add(orderKey);
        }

        if (hasDuplicates) {
          tableStore.normalizeRowOrder();
        }
      } else {
        // Group-row drag: keep the group + its subRows together as a unit.
        // Bypass the default arrayMoveImmutable path since it only moves the
        // group row itself, leaving subRows stranded at their old position.
        const activeRow = sourceRowId
          ? tableStore.getRowById(sourceRowId)
          : undefined;
        if (
          sourceRowId &&
          activeRow?.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G
        ) {
          // PSTUDIO-3160 (C): refuse drops flagged invalid during dragmove
          // (the red insertion line) — a group must not land inside another
          // group. moveRowGroup carries the same guard as a data backstop.
          if (tableStore.dragSession?.isInvalidDrop) {
            const wasMultiDrag = tableStore.dragSession?.isMulti ?? false;
            tableStore.endDragSession();
            if (!wasMultiDrag) {
              tableStore.deselectAllRows();
            }
            return;
          }
          // PSTUDIO-2349: derive targetOrder from the cursor-driven
          // `dragSession.insertionIndex` rather than `target.sortable.id`.
          // The latter is the same trap PSTUDIO-2406 already fixed for
          // single-row drag (see `handleDragMove`): once the dragged group's
          // CSS translation visually overlaps a drop target, dnd-kit reports
          // `target.sortable.id === sourceRowId`, so `overRow === activeRow`
          // and `targetOrder = activeRow.order` made every group drag a
          // silent no-op (group + subRows moved by +1 from current position,
          // never to the cursor's intended slot).
          const cursorInsertionIndex =
            tableStore.dragSession?.insertionIndex ?? null;
          const visibleRows = tableStore.getVisibleRows();
          const bundleIds = new Set<string>([
            sourceRowId,
            ...tableStore.rows
              .filter((r) => r.settings?.groupId === sourceRowId)
              .map((r) => r.id),
          ]);
          const oldIndex = visibleRows.findIndex((r) => r.id === sourceRowId);

          let targetOrder: number = 0;
          if (cursorInsertionIndex != null && oldIndex !== -1) {
            // Cursor's insertionIndex is computed against the ORIGINAL
            // visibleRows list (including the bundle). When we remove the
            // bundle and reinsert, indices past the bundle shift by the
            // count of *visible* bundle members (subRows are hidden when
            // the group is collapsed). Drops landing within the bundle's
            // own range are no-ops (user dropped the group on itself or
            // one of its subRows).
            const visibleBundleSize = visibleRows.filter((r) =>
              bundleIds.has(r.id)
            ).length;
            const bundleEndExclusive = oldIndex + visibleBundleSize;
            if (
              cursorInsertionIndex >= oldIndex &&
              cursorInsertionIndex <= bundleEndExclusive
            ) {
              // No-op: dropped on bundle's own range.
              const wasMultiDrag = tableStore.dragSession?.isMulti ?? false;
              tableStore.endDragSession();
              if (!wasMultiDrag) {
                tableStore.deselectAllRows();
              }
              return;
            }
            const targetIndexWithoutBundle =
              cursorInsertionIndex > oldIndex
                ? cursorInsertionIndex - visibleBundleSize
                : cursorInsertionIndex;
            const rowsWithoutBundle = visibleRows.filter(
              (r) => !bundleIds.has(r.id)
            );
            if (targetIndexWithoutBundle <= 0) {
              // Insert at top — anchor below a synthetic order=0 so the
              // group lands at 1, well below the first existing row.
              targetOrder = 0;
            } else {
              const clampedIndex = Math.min(
                targetIndexWithoutBundle,
                rowsWithoutBundle.length
              );
              const anchorRow = rowsWithoutBundle[clampedIndex - 1];
              targetOrder = anchorRow.order;
              // PSTUDIO-3160 (C): dropping below a COLLAPSED group header
              // means "after the whole group". Anchoring at the header's own
              // order would interleave the dropped bundle with the hidden
              // subRows once the group expands again.
              if (
                anchorRow.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G &&
                !anchorRow.settings?.groupExpanded
              ) {
                const childOrders = tableStore.rows
                  .filter((r) => r.settings?.groupId === anchorRow.id)
                  .map((r) => r.order);
                if (childOrders.length > 0) {
                  targetOrder = Math.max(...childOrders);
                }
              }
            }
          } else {
            // Fallback: no usable cursor index. Anchor at the very bottom
            // (matches the prior contract for "drop past the last visible
            // row").
            const maxOrder = tableStore.rows.reduce(
              (acc, r) => (r.order > acc ? r.order : acc),
              0
            );
            targetOrder = maxOrder + tableStore.orderIncrementV2;
          }
          tableStore.moveRowGroup(sourceRowId, targetOrder);
          const wasMultiDrag = tableStore.dragSession?.isMulti ?? false;
          tableStore.endDragSession();
          if (!wasMultiDrag) {
            tableStore.deselectAllRows();
          }
          return;
        }

        const sortedRows = [...tableStore.sortedRows];
        const oldIndex = sortedRows.findIndex((row) => row.id === sourceRowId);

        // Prefer the cursor-position-based insertion index from
        // handleDragMove. dnd-kit's `target.sortable.index` reports the
        // source row when its CSS translation overlaps the would-be drop
        // target, which makes single-row drag look like a no-op even when
        // the cursor is clearly over a different row. Fall back to
        // target.sortable.index when no cursor index is available (e.g.
        // keyboard sensor).
        const cursorInsertionIndex = tableStore.dragSession?.insertionIndex;
        let newIndex = -1;
        if (
          cursorInsertionIndex !== null &&
          cursorInsertionIndex !== undefined
        ) {
          // insertionIndex from handleDragMove is the slot the moved row
          // should occupy in the array AFTER it's been removed; convert it
          // to the equivalent position in the original array so the
          // arrayMoveImmutable below produces the right result.
          newIndex =
            cursorInsertionIndex > oldIndex
              ? cursorInsertionIndex - 1
              : cursorInsertionIndex;
        } else if (target && 'sortable' in target && target.sortable) {
          newIndex = (target.sortable as { index?: number }).index ?? -1;
        }

        if (newIndex === -1 || oldIndex === -1 || oldIndex === newIndex) {
          const wasMultiDrag = tableStore.dragSession?.isMulti ?? false;
          tableStore.endDragSession();
          if (!wasMultiDrag) {
            tableStore.deselectAllRows();
          }
          return;
        }

        // PSTUDIO-2406: fractional indexing for single-row reorder.
        // Re-numbering all rows to a clean (i+1)*ORDER_INCREMENT grid, as the
        // previous logic did, made the bulk save ship every row whose order
        // didn't already happen to land on that grid — i.e. ~all rows on a
        // 1700-node workflow whose orders had drifted off the grid through
        // past drags/inserts. Instead, place the moved row's new order
        // *between its new neighbours* (or just past one if at an edge), so
        // only the moved row's order changes and the bulk save ships exactly
        // one `{id, order}`. Fall back to a full renumber only when the gap
        // between neighbours has been depleted by previous fractional moves.
        const moved = sortedRows[oldIndex];
        const reordered = arrayMoveImmutable<TableNode>(
          sortedRows,
          oldIndex,
          newIndex
        );
        const leftNeighbor = newIndex > 0 ? reordered[newIndex - 1] : undefined;
        const rightNeighbor =
          newIndex < reordered.length - 1 ? reordered[newIndex + 1] : undefined;
        const leftOrder = leftNeighbor?.order ?? null;
        const rightOrder = rightNeighbor?.order ?? null;
        const increment = tableStore.orderIncrementV2;
        let proposedOrder: number | null = null;
        if (leftOrder !== null && rightOrder !== null) {
          if (rightOrder - leftOrder >= 2) {
            proposedOrder = Math.floor((leftOrder + rightOrder) / 2);
          }
        } else if (leftOrder !== null) {
          proposedOrder = leftOrder + increment;
        } else if (rightOrder !== null) {
          proposedOrder =
            rightOrder > 1 ? Math.floor(rightOrder / 2) : rightOrder - 1;
        } else {
          proposedOrder = increment;
        }

        if (
          proposedOrder !== null &&
          proposedOrder !== moved.order &&
          proposedOrder !== leftOrder &&
          proposedOrder !== rightOrder
        ) {
          // Fast path: only the moved row gets a new order. Bulk save sends
          // exactly one `{id, order}` regardless of dataset size.
          tableStore.reorderRows(new Map([[moved.id, proposedOrder]]));
        } else {
          // Slow path: gap exhausted (or the proposed value collides with a
          // neighbour). Re-number every row on a clean grid; this is the
          // pre-PSTUDIO-2406 behaviour and ships every row whose order
          // changed.
          const newOrderById = new Map(
            reordered.map((row, i) => [row.id, increment * (i + 1)])
          );
          tableStore.reorderRows(newOrderById);
        }

        // PSTUDIO-2406 spike: O(n) duplicate-order check (see multi-drag path
        // above for rationale).
        const seenOrders = new Set<number>();
        let hasDuplicates = false;
        for (const row of tableStore.sortedRows) {
          const orderKey = row.order ?? 0;
          if (seenOrders.has(orderKey)) {
            hasDuplicates = true;
            break;
          }
          seenOrders.add(orderKey);
        }

        // backup check to make sure no two rows have the same order
        if (hasDuplicates) {
          tableStore.normalizeRowOrder();
        }
      }

      const wasMultiDrag = tableStore.dragSession?.isMulti ?? false;
      tableStore.endDragSession();

      if (!wasMultiDrag) {
        tableStore.deselectAllRows();
      }

      // PSTUDIO-2220: reconcile group membership after the drag actually
      // placed the row(s) into their new visual position. Without this the
      // group's count badge stays stale, copy/paste skips the new visual
      // member (settings.subRows is the source of truth for both), and a
      // subRow dragged past the group's last visible member is still tracked
      // as a member at the data layer. We deliberately skip this for the
      // G-branch: a moved group must not pull unrelated rows that the
      // bundle's translation happened to cross over.
      if (sourceRowId) {
        const movedRow = tableStore.getRowById(sourceRowId);
        if (movedRow?.nodeType !== Tenancy_Node_Type_Enum_Ps_Enum.G) {
          if (isMultiRowDrag) {
            // Each moved row reconciles based on its final position; iterate
            // top-down so the sosed-sverhu chain reflects each prior update.
            const movedSorted = tableStore.sortedRows.filter((r) =>
              selectedRowIds.includes(r.id)
            );
            for (const movedRowItem of movedSorted) {
              tableStore.syncGroupMembershipForRow(movedRowItem.id);
            }
          } else {
            tableStore.syncGroupMembershipForRow(sourceRowId);
          }
        }
      }

      // Post-reorder consolidation for group rows: regardless of which
      // branch above persisted the new order (group-intercept, multi-drag,
      // or default single-row fractional indexing), if the moved row is a
      // group its subRows MUST trail it. Without this the default path
      // (which is what runs whenever dnd-kit fires drag-end before the
      // intercept can read a fully-populated dragSession) leaves children
      // at their old orders — the group appears detached from its subRows
      // visually. This loop is a no-op when sourceRowId isn't a group.
      if (sourceRowId) {
        const movedRow = tableStore.getRowById(sourceRowId);
        if (movedRow?.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G) {
          const groupOrder = movedRow.order;
          const children = tableStore.rows.filter(
            (r) => r.settings?.groupId === sourceRowId
          );
          if (children.length > 0) {
            emitOnce(() => {
              children.forEach((child, index) => {
                tableStore.updateRow({
                  rowId: child.id,
                  updates: { order: groupOrder + (index + 1) },
                  skipValidationRules: true,
                  saveImmediately: true,
                });
              });
            });
          }
        }
      }
    });

    // PSTUDIO-2154: register the net order change as one undo/redo command.
    // Reuses _applyOrders for both directions (which also resyncs each affected
    // group's settings.subRows), and reveals the dragged row after undo/redo.
    tableStore.recordReorderCommand(reorderBefore, sourceRowId);
  };

  const handleDragStart: DragDropEvents['dragstart'] = (event) => {
    const sourceRowId = (
      event.operation.source as { sortable?: { id?: string } }
    )?.sortable?.id;

    if (!sourceRowId) {
      return;
    }

    const isSelected = tableStore.selectedRows.has(sourceRowId);

    // PSTUDIO-3160 (C): a group header must never travel inside a multi-row
    // drag. The multi-drag path reorders via reorderRows and bypasses every
    // group guard (red insertion line, drag-end refusal, moveRowGroup
    // backstop), so a bundled header could land inside another group's
    // range — and a partial selection (header without all of its children)
    // would scatter the group. Range selection has no nodeType filter, so a
    // header CAN sit in selectedRows; groups are dragged solo by their own
    // handle instead, where all the guards live.
    const selectionContainsGroupHeader = [
      ...tableStore.selectedRows.values(),
    ].some(
      (id) =>
        tableStore.getRowById(id)?.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G
    );

    const canMultiDrag =
      isSelected &&
      tableStore.selectedRows.size > 1 &&
      tableStore.isSelectionContinuous &&
      !tableStore.isMetaKeyPressed &&
      !selectionContainsGroupHeader;

    const selectedIds = canMultiDrag
      ? [...tableStore.selectedRows.values()]
      : [sourceRowId];
    const totalHeight = canMultiDrag
      ? calculateSelectedRowsHeight(selectedIds, rowRefs.current)
      : 0;

    // Root cause: a single group-row drag previously passed groupHeight=0
    // to startDragSession, so drag-shadow-portal early-returned and the
    // user saw only dnd-kit's native source-row translation — no bundle
    // preview covering the group's children (PSTUDIO-3063 #5). For a solo
    // group-row drag we compute a bundle height from the group row plus
    // its visible (non-hidden) subRows and pass that through; selectedIds
    // stays single-row so handleDragMove and handleDragEnd continue to
    // treat this as a single-row drag (the group intercept in
    // handleDragEnd already handles bundle movement via moveRowGroup).
    const sourceRow = tableStore.getRowById(sourceRowId);
    const isGroupRowSoloDrag =
      !canMultiDrag && sourceRow?.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G;
    let bundleHeight = totalHeight;
    if (isGroupRowSoloDrag) {
      const visibleChildIds: string[] = (
        sourceRow?.settings?.subRows ?? []
      ).filter((id) => tableStore.getRowById(id)?.hidden !== true);
      bundleHeight = calculateSelectedRowsHeight(
        [sourceRowId, ...visibleChildIds],
        rowRefs.current
      );
    }

    let initialTop = 0;
    let initialLeft = 0;
    let initialWidth = 0;
    const sourceRowEl = rowRefs.current.get(sourceRowId);
    if (sourceRowEl) {
      const rect = sourceRowEl.getBoundingClientRect();
      initialTop = rect.top;
      initialLeft = rect.left;
      initialWidth = rect.width;
    }

    const scrollContainer = tableScrollerRef.current;
    const viewportHeight = scrollContainer?.clientHeight ?? window.innerHeight;

    tableStore.startDragSession({
      activeRowId: sourceRowId,
      selectedRowIds: selectedIds,
      groupHeight: bundleHeight,
      initialTop,
      initialLeft,
      initialWidth,
      viewportHeight,
    });

    if (tableStore.isOversizeDrag && canMultiDrag && scrollContainer) {
      const { sortedRows } = tableStore;
      for (let i = 0; i < sortedRows.length; i++) {
        if (selectedIds.includes(sortedRows[i].id)) {
          const firstRowEl = rowRefs.current.get(sortedRows[i].id);
          if (firstRowEl) {
            const containerRect = scrollContainer.getBoundingClientRect();
            const rowRect = firstRowEl.getBoundingClientRect();
            const headerOffset = 30;
            const scrollOffset =
              rowRect.top -
              containerRect.top +
              scrollContainer.scrollTop -
              headerOffset;
            scrollContainer.scrollTop = Math.max(0, scrollOffset);
          }
          break;
        }
      }
    }

    emitOnce(() => {
      tableStore.deselectAllCells();
      if (!tableStore.isOversizeDrag && (!isSelected || !canMultiDrag)) {
        tableStore.deselectAllRows();
        tableStore.selectRow(sourceRowId);
      }
    });
  };

  // Compute insertion index from cursor position. Tracked for both single
  // and multi-drag. dnd-kit's `target.sortable.index` (used pre-PSTUDIO-2406)
  // becomes unreliable for single-row drag once the dragged row's CSS
  // translation visually overlaps the would-be drop target — the reported
  // target collapses back to the source row, so the dragend handler treats
  // it as a no-op. Reading the live cursor against each row's bounding box
  // bypasses that, since the rect of an untransformed neighbour row is
  // unaffected by the source row's translation.
  const handleDragMove: DragDropEvents['dragmove'] = (event) => {
    if (!tableStore.dragSession) return;

    const pointerY = (event as any).operation?.position?.current?.y;
    if (pointerY == null) return;

    const rows = tableStore
      .getAllSortedRows()
      .filter((row) => row.hidden !== true);

    // PSTUDIO-2349: when the dragged source is a GROUP row, skip the
    // group + its visible subRows when scanning for the insertion slot.
    // dnd-kit's CSS transform pins the dragged group's bounding rect to
    // the cursor (the drag handle is the chevron at the TOP of the row,
    // so the row's midpoint sits below `pointerY`), making the loop break
    // at the bundle's own sorted index and return insertionIndex =
    // oldIndex. The drag-end handler then correctly treats that as "drop
    // on self" and no-ops, so every group drag became silent. Single-row
    // drags are left as-is — they have their own oldIndex-vs-newIndex
    // adjustment downstream and worked before this patch.
    const sourceRowId = tableStore.dragSession?.activeRowId;
    const sourceRow = sourceRowId ? tableStore.getRowById(sourceRowId) : null;
    const isGroupDrag =
      sourceRow?.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G;

    // Skip the source row(s) in the loop. dnd-kit CSS-translates the dragged
    // row(s) so their bounding-box rects follow the cursor — using those rects
    // would give midpoints ≈ cursor, locking insertionIndex to source's own
    // index every move. (Multi-drag is unaffected because a separate overlay
    // is rendered while the original rows keep their static rects, but
    // single-row drag CSS-transforms the actual row.)
    const { dragSession } = tableStore;
    const skipIds = new Set<string>(
      dragSession?.isMulti
        ? (dragSession.selectedRowIds ?? [])
        : dragSession?.activeRowId
          ? [dragSession.activeRowId]
          : []
    );

    // PSTUDIO-2349: when the dragged source is a GROUP row, also skip its
    // visible subRows. The group's CSS-transformed rect pins to the cursor,
    // so without skipping the whole bundle the loop locks insertionIndex to
    // the bundle's own index and the group drag silently no-ops.
    if (isGroupDrag && sourceRowId) {
      skipIds.add(sourceRowId);
      tableStore.rows
        .filter((r) => r.settings?.groupId === sourceRowId)
        .forEach((r) => skipIds.add(r.id));
    }

    let insertionIndex: number | null = null;
    let lastNonSkippedIndex = -1;

    for (let i = 0; i < rows.length; i++) {
      if (skipIds.has(rows[i].id)) continue;
      const rowEl = rowRefs.current.get(rows[i].id);
      if (!rowEl) continue;

      const rect = rowEl.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;

      if (pointerY < midpoint) {
        insertionIndex = i;
        break;
      }
      lastNonSkippedIndex = i;
    }

    // Cursor below every non-skipped row's midpoint → "drop at end".
    // `lastNonSkippedIndex + 1` is correct even when the dragged bundle
    // sits at the tail of the sorted list, since the +1 anchors past the
    // last *other* row's slot rather than past the (skipped) source.
    if (insertionIndex === null && lastNonSkippedIndex !== -1) {
      insertionIndex = lastNonSkippedIndex + 1;
    }

    // Multi-drag: filter no-op insertions (where the cursor is between the
    // already-selected rows, i.e. the move would be a no-op since the
    // selection is contiguous and stays contiguous).
    if (tableStore.dragSession?.isMulti && insertionIndex !== null) {
      const selectedIds = tableStore.dragSession?.selectedRowIds || [];
      const selectedIndices = selectedIds
        .map((id) => rows.findIndex((r) => r.id === id))
        .filter((i) => i !== -1)
        .sort((a, b) => a - b);

      if (selectedIndices.length > 0) {
        const minSelected = selectedIndices[0];
        const maxSelected = selectedIndices[selectedIndices.length - 1];

        if (insertionIndex > minSelected && insertionIndex <= maxSelected + 1) {
          insertionIndex = null;
        }
      }
    }

    // PSTUDIO-3160 (C): a group cannot be dropped inside another group. The
    // slot is invalid when the visible row it sits above is a subRow of an
    // (expanded) group — i.e. the insertion point falls strictly between a
    // group header and its last child. The insertion line turns red and
    // handleDragEnd refuses the drop. (SubRows of collapsed groups are
    // hidden, so they never appear in `rows` — collapsed groups are handled
    // by the drag-end anchor adjustment instead.)
    let isInvalidDrop = false;
    if (isGroupDrag && insertionIndex !== null) {
      const slotRow = rows[insertionIndex];
      isInvalidDrop =
        !!slotRow && !skipIds.has(slotRow.id) && !!slotRow.settings?.groupId;
    }

    tableStore.updateInsertionIndex(insertionIndex, isInvalidDrop);
  };

  const handleResizeStart = useCallback(
    (rowId: string) => {
      const rowElement = rowRefs.current?.get(rowId);
      if (!rowElement) return;

      const rect = rowElement.getBoundingClientRect();
      resizeRowInitialTopRef.current = rect.top;

      // Get current row height from store or use default
      const currentSettings = tableStore.getValue(rowId, NodeColumns.settings);
      const currentHeight =
        currentSettings?.rowHeight || rowElement.offsetHeight;

      resizingRowIdRef.current = rowId;
      tableStore.setResizingRowId(rowId);
      resizeHeightRef.current = currentHeight;

      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleResizeMouseUp);
    },
    [handleResizeMouseMove, handleResizeMouseUp]
  );

  const handleResetSize = useCallback((rowId: string) => {
    const { selectedRows } = tableStore;
    if (selectedRows.size > 1 && selectedRows.has(rowId)) {
      [...selectedRows.values()].map((value) => {
        // Save row height to table store settings using ref to get latest value
        tableStore.updateRow({
          rowId: value,
          updates: {
            settings: {
              ...tableStore.getValue(value, NodeColumns.settings),
              rowHeight: MIN_ROW_HEIGHT,
            },
          },
          skipValidationRules: true,
          saveImmediately: true,
        });
      });
    } else {
      tableStore.updateRow({
        rowId,
        updates: {
          settings: {
            ...tableStore.getValue(rowId, NodeColumns.settings),
            rowHeight: MIN_ROW_HEIGHT,
          },
        },
        skipValidationRules: true,
        saveImmediately: true,
      });
    }
  }, []);

  // (tableScrollerRef declared earlier so the virtualizer can use it.)
  useEffect(() => {
    if (tableScrollerRef.current && !tableStore.tableScrollerRef) {
      tableStore.tableScrollerRef = tableScrollerRef.current;
    }
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for common navigation keys like Tab, arrow keys, Enter
      // Enter is included so that when table-nav moves focus to the cell below,
      // the new cell's handleFocus sees isKeyboardNav=true and sets selection (is-selection-start).
      const isArrowKey =
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight';

      if (e.key === 'Tab' || e.key === 'Enter' || isArrowKey) {
        tableStore.setKeyboardNav(true);
      }

      // PSTUDIO-2372: flag a Shift+Arrow range extension BEFORE table-nav
      // moves focus (this listener is capture-phase), so cell.tsx's
      // handleFocus/handleBlur skip their selection reset and the range
      // anchor survives the focus move. Any other key (incl. Shift+Tab /
      // Shift+Enter) clears the flag so their re-anchoring stays intact.
      tableStore.setShiftArrowNav(isArrowKey && e.shiftKey);

      // PSTUDIO-1964: Enter/Space on the row-number (index) cell selects its
      // row — the keyboard twin of clicking the index cell. Handled HERE in the
      // CAPTURE phase (before the type-to-edit block below AND before
      // table-nav's own bubble-phase Enter handler) because that handler
      // focuses the index cell's dnd-kit drag-handle <div> (it carries a
      // runtime tabindex, so it matches table-nav's Focusable selector), moving
      // document.activeElement OFF the index <td> before a bubble-phase
      // 'enter'/'space' hotkey could read it — the hotkey then sees the <div>,
      // not the <td>, and no-ops. In the capture phase the index <td> is still
      // focused, so this reliably selects the row. The index cell has no
      // editable content, so it never shadows a real cell edit. Shift+Space is
      // excluded here — it is the any-cell row-select hotkey (§2c) and reaches
      // its handler unaffected (table-nav ignores Space).
      if (
        (e.key === 'Enter' || e.key === ' ') &&
        !e.shiftKey &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !tableStore.isCellBeingEdited
      ) {
        const activeTd = document.activeElement;
        if (
          activeTd instanceof HTMLTableCellElement &&
          activeTd.classList.contains('c-editor-table-td--index-cell')
        ) {
          const rowId = activeTd
            .closest('tr[data-rowid]')
            ?.getAttribute('data-rowid');
          if (rowId) {
            e.preventDefault();
            e.stopPropagation();
            tableStore.selectFocusedRowByKeyboard(rowId);
            activeTd.blur();
            return;
          }
        }
      }

      if (
        e.key === 'Tab' &&
        // PSTUDIO-1433: gate on WHERE THE FOCUS IS, not on isCellBeingEdited
        // alone — focus in the Expanded Cell View flips that flag too, and
        // capturing Tab there swallowed the key for nothing (the panel editor's
        // active element has no `td` ancestor, so the `currentTd` branch below
        // was skipped), preventing the panel editor's own Tab = indent. Pure
        // predicate, unit tested in
        // expanded-cell-view/__tests__/should-grid-handle-tab-commit.
        shouldGridHandleTabCommit({
          isCellBeingEdited: tableStore.isCellBeingEdited,
          activeElement: document.activeElement,
        })
      ) {
        e.preventDefault();
        // This handler fully performs the commit + focus move. Without
        // stopPropagation the event also reaches table-nav's cellNavigation,
        // which blurs the td we just focused and re-focuses it one rAF later —
        // leaving focus on <body> in the gap. If the Tab KEYUP fires in that
        // gap (busy main thread), table-nav's table-level keyup listener never
        // sees it, its pressed-keys stack goes stale, and its `length <= 2`
        // gate silently disables ALL grid navigation (flaky Shift+Tab e2e).
        e.stopPropagation();

        const activeEl = document.activeElement as HTMLElement;
        const currentTd = activeEl?.closest('td');

        if (currentTd) {
          // PSTUDIO-2821: Tab is an EXPLICIT commit. Flag it before blur — the
          // control blurs before focus lands on the next td, so relatedTarget is
          // null and handleCellBlur would otherwise keep the cell open. The
          // commit (via handleCellBlur → commitCellEditing) also clears
          // isCellBeingEdited, so the manual reset is no longer needed.
          tableStore.setCellCloseIntent('commit');
          activeEl?.blur();

          const allTds = Array.from(
            currentTd.closest('tbody')?.querySelectorAll('td[data-row-id]') ||
              []
          );
          const currentIndex = allTds.indexOf(currentTd);
          const nextTd = e.shiftKey
            ? allTds[currentIndex - 1]
            : allTds[currentIndex + 1];

          if (nextTd instanceof HTMLTableCellElement) {
            nextTd.focus();
            tableStore.selectCellViaKeyboardNavigation(false);
          }
        }
      }

      // Release 29817 review: Esc in a grid cell's editable select (Analytics
      // Events) with its list CLOSED hands focus back to the td, as Esc in the
      // other cell editors does through table-nav. The select stops the key in
      // its shadow root, so table-nav never sees it; this capture listener does.
      // Ahead of the panel's Esc below: with the panel open, the first Esc
      // leaves the cell's select and the next closes the panel — the order an
      // in-cell text edit gets, because that branch is skipped while
      // editingRowId/editingColumnId are set.
      const escapeCell = cellToFocusOnClosedSelectEscape(e);
      if (escapeCell) {
        e.preventDefault();
        escapeCell.focus();
        return;
      }

      // PSTUDIO-3624: Esc closes the Expanded Cell View panel. The panel has no
      // in-panel close button (design), so Esc / Shift+Enter / the toolbar
      // toggle are the only ways to close it. When an INLINE cell edit is in
      // progress (editingRowId/editingColumnId set), defer to the existing
      // cancel-edit behavior on that first Esc and don't close the panel. Focus
      // in the panel's OWN textarea does NOT set those ids, so Esc there still
      // closes the panel — blur it first so its onBlur persists the edit before
      // the panel unmounts.
      // PSTUDIO-4784: the panel's Analytics Events select is such a control that
      // sets neither id. While its list is open (which includes a half-typed
      // value) Esc is the select's — it drops the text and closes the list — and
      // the panel closes on the next Esc.
      if (
        e.key === 'Escape' &&
        tableStore.expandedCellViewOpen &&
        tableStore.editingRowId == null &&
        tableStore.editingColumnId == null &&
        !isOpenEditableSelect(e.target)
      ) {
        e.preventDefault();
        e.stopPropagation();
        // NO manual blur here (PSTUDIO-1433). It used to exist so the panel
        // textarea's `onBlur` would persist the edit before the unmount; the
        // panel now commits from its teardown effect either way, so the blur
        // bought nothing — and it actively broke the focus hand-back, because
        // `setExpandedCellViewOpen` decides whether to return focus by asking
        // where focus IS, and a blur had already moved it to <body> (measured:
        // Esc left focus on <body> and the grid unresponsive).
        tableStore.setExpandedCellViewOpen(false);
        return;
      }

      // PSTUDIO-3623: Esc closes the keyboard-shortcuts help panel (like the
      // Expanded Cell View above). The panel is a non-blocking sidebar, so an
      // inline cell edit can be in progress underneath it — defer to that edit's
      // own cancel-Esc on the first Esc (same guard as the Expanded Cell View
      // branch above), so the edit isn't left open/uncommitted and swallowed by
      // this capture-phase stopPropagation. The panel then closes on a bare Esc.
      if (
        e.key === 'Escape' &&
        tableStore.keyboardShortcutsOpen &&
        tableStore.editingRowId == null &&
        tableStore.editingColumnId == null &&
        !isOpenEditableSelect(e.target)
      ) {
        e.preventDefault();
        e.stopPropagation();
        tableStore.setKeyboardShortcutsOpen(false);
        return;
      }

      // PSTUDIO-3623: ⌘/ (Ctrl+/) toggles the keyboard-shortcuts help panel.
      // Handled here (capture phase, like Shift+Enter) so it can enforce the
      // "one sidebar panel at a time" rule via closeOverlaySidePanels() — which
      // lives in this component's React state, unreachable from a store hotkey.
      const shortcutsTargetEl = e.target as HTMLElement;
      const shortcutsIsInInput =
        shortcutsTargetEl instanceof HTMLInputElement ||
        shortcutsTargetEl instanceof HTMLTextAreaElement ||
        isEditableSelectTarget(shortcutsTargetEl);
      if (
        shouldToggleKeyboardShortcutsPanel(e, {
          isEditing: tableStore.isCellBeingEdited,
          isInInput: shortcutsIsInInput,
        })
      ) {
        e.preventDefault();
        e.stopPropagation();
        if (!tableStore.keyboardShortcutsOpen) {
          closeOverlaySidePanels();
        }
        tableStore.toggleKeyboardShortcutsOpen();
        return;
      }

      // PSTUDIO-3624: Shift+Enter toggles the Expanded Cell View panel. It fires
      // regardless of selection count (0, 1, or many) so the panel can be opened
      // with nothing selected. Placed BEFORE the in-edit Enter block below so the
      // guard order is explicit and the in-edit path (commit + move up) is never
      // shadowed. The panel textarea's own Shift+Enter is preserved by the
      // isInInput guard.
      const toggleTargetEl = e.target as HTMLElement;
      const toggleIsInInput =
        toggleTargetEl instanceof HTMLInputElement ||
        toggleTargetEl instanceof HTMLTextAreaElement ||
        isEditableSelectTarget(toggleTargetEl);
      if (
        shouldToggleExpandedCellView(e, {
          isEditing: tableStore.isCellBeingEdited,
          isInInput: toggleIsInInput,
        })
      ) {
        e.preventDefault();
        e.stopPropagation();
        // PSTUDIO-3624: opening via Shift+Enter must honor sidebar mutual
        // exclusivity too (the toolbar toggle does this via
        // closeOverlaySidePanels) — close the overlay panels when OPENING.
        if (!tableStore.expandedCellViewOpen) {
          closeOverlaySidePanels();
        }
        tableStore.toggleExpandedCellViewOpen();
        // Release 29817: on an Analytics Events cell the panel's event select is
        // otherwise reachable by mouse only — the cell is a select, so no edit
        // entry ever bumps the autofocus nonce for it. Ask the panel to focus
        // its select. Only this column asks; on the others the panel still
        // takes focus on mount once the nonce is non-zero (it is never reset —
        // see shouldFocusPanelSelectOnKeyboardOpen).
        if (
          shouldFocusPanelSelectOnKeyboardOpen({
            isPanelOpenAfterToggle: tableStore.expandedCellViewOpen,
            columnId: tableStore.selectedCell()?.columnId,
            panelRendered: tableStore.shouldRenderExpandedCellView,
          })
        ) {
          tableStore.requestExpandedCellViewAutofocus();
        }
        return;
      }

      // PSTUDIO-2821: Enter / Shift+Enter while EDITING = explicit COMMIT + a
      // VERTICAL move (down / up) — Google-Sheets parity. Mirrors the Tab block's
      // commit choreography (setCellCloseIntent('commit') + blur, capture-phase +
      // stopPropagation so table-nav's own Enter doesn't ALSO move focus and
      // corrupt its keyup pressed-keys stack), but moves to the same-column cell
      // one VISIBLE row below/above via commitAndMoveVertically. Snapshot
      // editingRowId/editingColumnId BEFORE blur — the commit clears them.
      // Cmd/Ctrl+Enter is excluded so the textarea newline (cell.tsx) survives.
      if (
        e.key === 'Enter' &&
        !e.metaKey &&
        !e.ctrlKey &&
        tableStore.isCellBeingEdited &&
        // PSTUDIO-2821: gate on the editing row/column ids, not isCellBeingEdited
        // alone. Only a real IN-CELL edit sets editingRowId/editingColumnId
        // (toggleCellInput, text/number cells). The controls-bar textarea also
        // flips isCellBeingEdited=true on focus but NEVER sets these — so without
        // this guard, pressing Enter in the controls bar enters this block, which
        // stopPropagation()s the controls bar's own Enter handler yet then can't
        // move (null ids), stranding the selection (the row below never gets
        // focus → is-selection-start never lands). It also correctly scopes the
        // block to text/number; select/checkbox/behaviors keep table-nav's
        // step-into nav and set no editing ids.
        tableStore.editingRowId != null &&
        tableStore.editingColumnId != null
      ) {
        e.preventDefault();
        e.stopPropagation();
        const { editingRowId } = tableStore;
        const { editingColumnId } = tableStore;
        const activeEl = document.activeElement as HTMLElement;
        tableStore.setCellCloseIntent('commit');
        activeEl?.blur();
        if (editingRowId && editingColumnId) {
          tableStore.commitAndMoveVertically(
            editingRowId,
            editingColumnId as NodeColumns,
            e.shiftKey ? 'up' : 'down'
          );
        }
      }

      // Start editing on printable key when a cell is selected but not being edited
      // Skip if focus is in an input element
      const target = e.target as HTMLElement;
      const isInInput =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        isEditableSelectTarget(target);

      if (
        !tableStore.isCellBeingEdited &&
        tableStore.selectedCells.length === 1 &&
        tableStore.isEditing() &&
        e.key.length === 1 &&
        // PSTUDIO-1964: Shift+Space is the row-selection shortcut (§2c), NOT
        // type-to-edit. Without this guard the capture-phase type-to-edit would
        // eat the Space (key === ' ', length 1) and stopPropagation before the
        // 'shift+space' hotkey fires — so Shift+Space from a focused data cell
        // (selectedCells.length === 1) never selected the row. Only Shift+Space
        // is excluded; other Shift+<char> combos (capitals, symbols) still
        // type-to-edit.
        !(e.shiftKey && e.key === ' ') &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !isInInput
      ) {
        e.preventDefault();
        e.stopPropagation();
        tableStore.startCellEditingWithChar(e.key);
      }

      // PSTUDIO-2821: Enter on a SELECTED but NOT-editing text/number cell OPENS
      // edit (Google-Sheets parity). Capture-phase + stopPropagation so table-nav
      // doesn't instead focus-into / move. Gated to text/number editable kinds
      // (select/checkbox/behaviors keep table-nav's W3C step-into navigation and
      // have no draft pipeline). The matching IN-EDIT Enter (commit + move
      // down/up) is the block above; this one is gated on !isCellBeingEdited so
      // the two never compete. A second Enter then lands in that block → commit +
      // move down, matching the Sheets open→commit+down two-press flow.
      if (
        e.key === 'Enter' &&
        !e.shiftKey &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !tableStore.isCellBeingEdited &&
        tableStore.isEditing() &&
        tableStore.selectedCells.length === 1
      ) {
        const td = (document.activeElement as HTMLElement | null)?.closest(
          'td[data-row-id]'
        ) as HTMLTableCellElement | null;
        const rowId = td?.dataset.rowId;
        const columnId = td?.dataset.cellId;
        const editKind = columnId
          ? getColumnEditKind(columnId as keyof TableRow)
          : null;
        if (
          td &&
          rowId &&
          columnId &&
          (editKind === 'text' || editKind === 'number')
        ) {
          e.preventDefault();
          e.stopPropagation();
          tableStore.toggleCellInput(td, rowId, columnId as keyof TableRow);
        }
      }
    };

    const handleMouseDown = () => {
      tableStore.setKeyboardNav(false);
      tableStore.setShiftArrowNav(false);
    };

    // Use capture phase so this runs before table-nav's keydown on the table.
    // That way when Enter moves focus to the cell below, the new cell's onFocus
    // already sees isKeyboardNav=true and applies selection (selectCell + setSelectedStartCell).
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [closeOverlaySidePanels]);

  const globalSearch = useRef<SearchPropsWithRef>(undefined);
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        tableStore.search(value);
      }, 200),
    []
  );

  // Keep the input in sync with the store. Cancel any pending debounced search
  // so that a queued call (e.g. from a fast user keystroke right before
  // `goToNode` cleared `searchQuery`) cannot overwrite the fresh value.
  useEffect(() => {
    debouncedSearch.cancel();
    setGlobalFilter(tableStore.searchQuery ?? '');
    // tableStore.searchQuery is a MobX observable; observer() handles reactivity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableStore.searchQuery, debouncedSearch]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    const handleGlobalSearchKeyDown = (e: KeyboardEvent) => {
      // Cmd+F / Ctrl+F - Focus global search
      if (
        e.keyCode === 114 ||
        (e.ctrlKey && e.keyCode === 70) ||
        (e.metaKey && e.keyCode === 70)
      ) {
        const globalSearchElement =
          globalSearch.current as unknown as HTMLInputElement;

        if (
          globalSearch.current &&
          globalSearchElement !== document.activeElement
        ) {
          e.preventDefault();
          globalSearchElement?.focus();
        } else {
          return true;
        }
      }
    };

    document.addEventListener('keydown', handleGlobalSearchKeyDown);

    return () => {
      document.removeEventListener('keydown', handleGlobalSearchKeyDown);
    };
  }, []);

  // const handleSelectionMouseDown =
  //   (rowId: string, columnId: string) => (e: React.MouseEvent) => {
  //     // Only start selection if clicking the cell background, not its contents
  //     if (e.target === e.currentTarget) {
  //       e.preventDefault();
  //       const selection = { rowId, columnId };
  //       tableStore.setSelectionStart(selection);
  //       tableStore.setSelectedCells([selection]);
  //     }
  //   };

  // const handleSelectionMouseEnter = (rowId: string, columnId: string) => () => {
  //   if (tableStore.selectionStart) {
  //     const selections = tableStore.getCellsBetween(tableStore.selectionStart, {
  //       rowId,
  //       columnId,
  //     });
  //     tableStore.setSelectedCells(selections);
  //   }
  // };

  // Add keyboard event listener for delete key
  // React.useEffect(() => {
  //   const handleKeyDown = (e: KeyboardEvent) => {
  //     if (
  //       e.key === 'Delete' ||
  //       (e.key === 'Backspace' && tableStore.selectedCells.length > 0)
  //     ) {
  //       tableStore.clearSelectedCells();
  //     }
  //   };

  //   window.addEventListener('keydown', handleKeyDown);
  //   return () => window.removeEventListener('keydown', handleKeyDown);
  // }, []);

  // PSTUDIO-2406 spike: derived values from the virtualizer (the hook itself
  // is set up earlier in the component, before useReactTable, so the windowed
  // data slice can be computed).
  const visibleTableRows = table
    .getRowModel()
    .rows.filter((row) => row.original.hidden !== true);
  const virtualItems = rowVirtualizer.getVirtualItems();
  const virtualTotalSize = rowVirtualizer.getTotalSize();
  const virtualPaddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const virtualPaddingBottom =
    virtualItems.length > 0
      ? virtualTotalSize - virtualItems[virtualItems.length - 1].end
      : 0;
  // Re-measure when row count changes (rows added/removed/reordered).
  useEffect(() => {
    rowVirtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalVisibleRowCount]);

  // PSTUDIO-2406 spike: stable per-rowId ref factory. Inline `ref={(el) => {}}`
  // recreates a new function every render, which causes React to call the
  // previous ref with `null` and then the new ref with the element. That
  // cycles `useSortable`'s registration in @dnd-kit on every parent re-render
  // and is a major source of lag and memory churn during drag (each drag move
  // updates dragSession observables → re-renders → ref churn → dnd-kit
  // re-registers all rendered rows). Caching the closure per rowId in a ref
  // keeps the ref reference identical across renders.
  const rowRefFactoryCache = useRef(
    new Map<string, (el: HTMLTableRowElement | null) => void>()
  );
  const getRowRefSetter = useCallback(
    (rowId: string) => {
      let cached = rowRefFactoryCache.current.get(rowId);
      if (!cached) {
        cached = (el: HTMLTableRowElement | null) => {
          if (el) {
            rowRefs.current.set(rowId, el);
            rowVirtualizer.measureElement(el);
          } else {
            rowRefs.current.delete(rowId);
            // Forget the cached closure too; the row may not come back, and
            // the closure holds a strong reference to rowVirtualizer.
            rowRefFactoryCache.current.delete(rowId);
          }
        };
        rowRefFactoryCache.current.set(rowId, cached);
      }
      return cached;
    },
    [rowVirtualizer]
  );

  // Same treatment for cell refs — keyed by `getCellKey(rowId, columnId)`.
  const cellRefFactoryCache = useRef(
    new Map<string, (el: HTMLTableCellElement | null) => void>()
  );
  const getCellRefSetter = useCallback((cellKey: string) => {
    let cached = cellRefFactoryCache.current.get(cellKey);
    if (!cached) {
      cached = (el: HTMLTableCellElement | null) => {
        if (el) {
          cellRefs.current.set(cellKey, el);
        } else {
          cellRefs.current.delete(cellKey);
          cellRefFactoryCache.current.delete(cellKey);
        }
      };
      cellRefFactoryCache.current.set(cellKey, cached);
    }
    return cached;
  }, []);

  return (
    <>
      <DragShadowPortal />
      {/* <div style={{ display: 'inline-block', marginLeft: '1rem' }}>
        <button
          onClick={() => tableStore.undo()}
          disabled={!tableStore.canUndo}
          title="Undo (Ctrl/Cmd + Z)"
          style={{ marginRight: '0.5rem' }}
        >
          ↩ Undo
        </button>
        <button
          onClick={() => tableStore.redo()}
          disabled={!tableStore.canRedo}
          title="Redo (Ctrl/Cmd + Shift + Z)"
        >
          ↪ Redo
        </button>
      </div> */}
      <div className="c-editor-table-bg"></div>
      <div className="c-editor-table-container">
        <Group orientation="horizontal" id="container">
          <Panel>
            {/* `disableCursor` — with the resize handle gone (PSTUDIO-3910) this
                group is not resizable, but the library still paints its
                `ns-resize` drag cursor whenever the pointer is over the
                toolbar/table boundary. Suppress it so the boundary is inert.
                NOT `disabled`, which would mark the panels aria-disabled and
                break typing in the toolbar's controls. */}
            <Group orientation="vertical" id="layout" disableCursor>
              <Panel
                id="toolbar-panel"
                defaultSize={toolbarSize}
                minSize={toolbarSize}
                maxSize={toolbarSize}
                className="c-editor-table-panel"
              >
                <ControlsBar
                  centerSlot={toolbarCenter}
                  viewOnly={isViewer}
                  openSupervisorPanel={() => {
                    // Opening Supervisor closes Expanded Cell View + the
                    // keyboard-shortcuts panel (PSTUDIO-3624 / PSTUDIO-3623).
                    if (!supervisorPanelOpen) {
                      tableStore.setExpandedCellViewOpen(false);
                      tableStore.setKeyboardShortcutsOpen(false);
                    }
                    showSupervisorPanel(!supervisorPanelOpen);
                  }}
                  supervisorPanelOpen={supervisorPanelOpen}
                  closeOverlaySidePanels={closeOverlaySidePanels}
                >
                  <Search
                    size="medium"
                    icon="filter-nodes"
                    style={{
                      width: 290,
                      marginBlock: -3,
                      marginInline: -19,
                    }}
                    placeholder="Filter nodes"
                    id="global-search"
                    test-id="ps-editor-table-search"
                    value={globalFilter}
                    // @ts-ignore
                    ref={globalSearch}
                    onfocus={() => {
                      tableStore.stopCellEditing();
                    }}
                    onInput={(e: Event) => {
                      const value = String(
                        (e.target as HTMLInputElement)?.value
                      );
                      // PSTUDIO-4023: THIS is the synchronous half of the
                      // search — `setGlobalFilter` re-runs the filtered row
                      // model on the spot, so the non-matching rows unmount in
                      // this very tick, while `tableStore.search()` (and the
                      // close signal it raises) only lands 200ms later.
                      //
                      // Measured live (6 Sep 2026): a user cannot reach this
                      // with a menu fully open — the popover's focus trap keeps
                      // the caret inside the menu (even Cmd+F cannot pull it
                      // out), so typing here always follows a click into the
                      // field, and that click's `mousedown` has already started
                      // the close. What remains is the ~50ms window while the
                      // menu is `closing` and its `ps-dropdown` is still
                      // projected into the portal: a keystroke landing inside
                      // it would unmount the row under the projection (the
                      // `removeChild` crash). Raising the signal here closes
                      // that window on the tick that drops the rows.
                      tableStore.requestCloseAllContextMenus();
                      setGlobalFilter(value);
                      debouncedSearch(value);
                    }}
                  ></Search>
                </ControlsBar>
              </Panel>
              <Panel
                id="content-panel"
                minSize={0}
                className="c-editor-table-panel"
              >
                <div className="c-editor-table-scroller" ref={tableScrollerRef}>
                  <div className="c-editor-table-wrapper">
                    <table
                      test-id="ps-editor-table"
                      id="ps-editor-table"
                      className={`c-editor-table ${tableStore.isEditing() ? 'c-editor-table--editing' : ''}`}
                      ref={tableRef}
                      {...listeners}
                    >
                      <thead className="c-editor-table__thead">
                        {table.getHeaderGroups().map((headerGroup) => (
                          <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                              if (header.id === 'index') {
                                return (
                                  <th
                                    test-id="c-editor-table--indexCell-th"
                                    ref={indexCellRef}
                                    key={header.id}
                                    className="c-editor-table__th-index-cell"
                                  >
                                    <span></span>
                                    <Tooltip
                                      content={`${tableStore.allGroupsExpanded ? 'Collapse' : 'Expand'} all node groups`}
                                    >
                                      <IconButton
                                        test-id="c-editor-table-toggle-all-groups"
                                        size="xsmall"
                                        name={
                                          tableStore.allGroupsExpanded
                                            ? 'chevrons-down-up'
                                            : 'chevrons-up-down'
                                        }
                                        style={{
                                          position: 'absolute',
                                          top: '50%',
                                          left: '50%',
                                          transform: 'translate(-50%, -50%)',
                                        }}
                                        onClick={() =>
                                          tableStore.toggleAllGroupRowsExpanded(
                                            !tableStore.allGroupsExpanded
                                          )
                                        }
                                      />
                                    </Tooltip>
                                  </th>
                                );
                              }

                              return (
                                <HeaderCell
                                  viewOnly={isViewer}
                                  key={header.id}
                                  header={header}
                                  // @ts-ignore
                                  hidden={header.column.columnDef.hidden}
                                />
                              );
                            })}
                          </tr>
                        ))}
                        {/* {table.getHeaderGroups().map((headerGroup) => (
                          <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                              if (header.id === 'index')
                                return (
                                  <th
                                    ref={indexCellRef}
                                    key={header.id}
                                    className="c-editor-table__th-index-cell"
                                    test-id="c-editor-table--indexCell-th"
                                  >
                                    <span></span>
                                  </th>
                                );

                              return (
                                <HeaderCell key={header.id} header={header} />
                              );
                            })}
                          </tr>
                        ))} */}
                      </thead>

                      <tbody>
                        <DragDropProvider
                          onDragEnd={isViewer ? undefined : handleDragEnd}
                          onDragStart={isViewer ? undefined : handleDragStart}
                          onDragMove={isViewer ? undefined : handleDragMove}
                          sensors={[
                            PointerSensor.configure({
                              activationConstraints: [
                                new PointerActivationConstraints.Distance({
                                  value: 3,
                                }),
                              ],
                            }),
                            KeyboardSensor,
                          ]}
                          modifiers={[
                            RestrictToElement.configure({
                              element: tableScrollerRef.current,
                            }),
                            RestrictToVerticalAxis,
                          ]}
                        >
                          {virtualPaddingTop > 0 && (
                            <tr
                              aria-hidden
                              className="c-editor-table-tr--virtual-spacer"
                              style={{ height: `${virtualPaddingTop}px` }}
                              // table-nav contract: spacers carry no
                              // navigable cells — skip so focus never lands
                              // here (PSTUDIO-3168).
                              data-nav-skip="true"
                            >
                              <td colSpan={table.getAllColumns().length} />
                            </tr>
                          )}
                          {virtualItems.map((virtualRow) => {
                            // virtualRow.index is the full-dataset index;
                            // useReactTable sees the windowed slice starting
                            // at tableStore.virtualWindowStart, so we offset
                            // here to look up the corresponding TanStack Row.
                            const localIndex =
                              virtualRow.index - tableStore.virtualWindowStart;
                            const row = visibleTableRows[localIndex];
                            if (!row) return null;
                            const rowId = row.original.id;
                            const visibleCells = row.getVisibleCells();
                            return (
                              <DraggableRow
                                key={rowId}
                                rowId={rowId}
                                ref={getRowRefSetter(rowId)}
                                onResizeStart={handleResizeStart}
                                onResetSize={handleResetSize}
                                disableDrag={isViewer}
                                virtualIndex={virtualRow.index}
                                indexCellWidth={indexCellWidth}
                                // PSTUDIO-3404: the group-header branch renders
                                // one real <td> per column with true pinning
                                // styles, so it needs the row's visible columns.
                                // Only group rows consume it — pass `undefined`
                                // for regular rows so the prop stays a stable
                                // reference for the dominant (non-group) case
                                // and we skip the per-row column allocation.
                                groupColumns={
                                  row.original.nodeType ===
                                  Tenancy_Node_Type_Enum_Ps_Enum.G
                                    ? visibleCells.map((cell) => cell.column)
                                    : undefined
                                }
                              >
                                {visibleCells.map((cell) => {
                                  const key = getCellKey(
                                    cell.row.id,
                                    cell.column.id
                                  );

                                  return (
                                    <Cell
                                      key={cell.id}
                                      cell={cell}
                                      rowId={row.original.id}
                                      ref={getCellRefSetter(key)}
                                      // @ts-ignore
                                      hidden={cell.column.columnDef.hidden}
                                    />
                                  );
                                })}
                              </DraggableRow>
                            );
                          })}
                          {virtualPaddingBottom > 0 && (
                            <tr
                              aria-hidden
                              className="c-editor-table-tr--virtual-spacer"
                              style={{
                                height: `${virtualPaddingBottom}px`,
                              }}
                              // table-nav contract: spacers carry no
                              // navigable cells — skip so focus never lands
                              // here (PSTUDIO-3168).
                              data-nav-skip="true"
                            >
                              <td colSpan={table.getAllColumns().length} />
                            </tr>
                          )}
                        </DragDropProvider>
                      </tbody>
                    </table>

                    {/* PSTUDIO-3623: the Add-Node / Resource Library FAB is
                        `position: fixed` (escapes the panel Group's stacking
                        context), so it floats OVER any open 500px right-side
                        panel's bottom-right corner. Hide it while ANY overlay
                        side panel is open so the panel reads as a clean surface
                        (fixes the overlap for Keyboard Shortcuts + the same
                        pre-existing overlap for Expanded Cell View / Supervisor
                        / ResourceLib). It returns the moment every panel closes. */}
                    {resourceLibraryData?.length &&
                    resourceLibDragDropAccess !== 'allowed' &&
                    !tableStore.keyboardShortcutsOpen &&
                    !tableStore.expandedCellViewOpen &&
                    !supervisorPanelOpen &&
                    !resourceLibPanelOpen ? (
                      <ResourceLibControls
                        isViewer={isViewer}
                        onAddRow={handleAddRow}
                        onOpenPanel={handleOpenResourceLib}
                      />
                    ) : null}
                  </div>
                </div>
                <ContextMenuScrollCloser />
                <TableFooter isViewer={isViewer} />
                {/* Project Bobcat (Resource Library drag-and-drop): the same
                    pill the canvas shows, in place of the FAB above. It sits in
                    this panel, not `position: fixed`, so a docked side panel
                    narrows the panel and the pill moves with its edge instead
                    of being hidden. Add Node stays in the footer. */}
                {resourceLibraryData?.length &&
                resourceLibDragDropAccess === 'allowed' ? (
                  <ResourceLibraryFab
                    className="c-resource-library-fab--table"
                    active={resourceLibPanelOpen}
                    onClick={handleOpenResourceLib}
                  />
                ) : null}
                {/**
                <Info
                  testId="ps-editor-table-info"
                  projectName={projectName || ''}
                  botName={botName || ''}
                  decisionNodes={tableStore.nodeCounts.decisionNodes}
                  actionNodes={tableStore.nodeCounts.actionNodes}
                  totalNodes={tableStore.nodeCounts.totalNodes}
                />
                */}
              </Panel>
            </Group>
          </Panel>
          {/* sidebarOpen && (
                <Panel
                  defaultSize={sidebarSize}
                  minSize={sidebarSize}
                  maxSize={sidebarSize}
                  className="c-editor-table__layout-sidepanel"
                >
                  <EmbedPanel onClose={() => showSidebar(false)} />
                </Panel>
              ) */}
          {/* addEmbedsPanelOpen && (
                <Panel
                  defaultSize={sidebarSize}
                  minSize={sidebarSize}
                  maxSize={sidebarSize}
                  className="c-editor-table__layout-sidepanel"
                >
                  <AddEmbedsPanel onClose={() => showEmbedsPanel(false)} />
                </Panel>
              ) */}
          {supervisorPanelOpen && (
            <Panel
              disabled
              defaultSize={sidebarSize}
              minSize={sidebarSize}
              maxSize={sidebarSize}
              className="c-editor-table__layout-sidepanel c-editor-table__layout-sidepanel--overflow-visible"
            >
              <SupervisorPanel
                viewOnly={isViewer}
                onClose={() => showSupervisorPanel(false)}
              />
            </Panel>
          )}
          {resourceLibPanelOpen && (
            <Panel
              disabled
              defaultSize={sidebarSize}
              minSize={sidebarSize}
              maxSize={sidebarSize}
              className="c-editor-table__layout-sidepanel"
            >
              <ResourceLibPanel
                viewOnly={isViewer}
                resourceLibData={resourceLibraryData}
                onClose={() => showResourceLibPanel(false)}
              />
            </Panel>
          )}
          {tableStore.shouldRenderNodePropertyPanel && (
            // Project Bobcat — the NODE property editor. Mutually exclusive
            // with the Expanded Cell View below: the store computed is true
            // only when the flag is on AND the selection resolves to a single
            // node, so the two can never occupy the sidebar at once. Same fixed
            // width and same no-`disabled` rule as the panel below.
            <Panel
              defaultSize={sidebarSize}
              minSize={sidebarSize}
              maxSize={sidebarSize}
              className="c-editor-table__layout-sidepanel"
            >
              <NodePropertyPanel
                viewOnly={isViewer}
                onClose={() => tableStore.setExpandedCellViewOpen(false)}
              />
            </Panel>
          )}
          {tableStore.shouldRenderExpandedCellView && (
            // Gated on the store computed, not `expandedCellViewOpen` directly:
            // with the property-editor flag on this panel is REPLACED by the
            // node panel above, so it renders only with the flag off — where
            // its behaviour is unchanged from today.
            // PSTUDIO-1433: the Expanded Cell View panel is RESIZABLE (Leo, 21
            // Aug) — a Separator drag handle plus a [MIN, MAX] range, replacing
            // the former fixed 500px (minSize===maxSize===sidebarSize). The
            // chosen width is persisted (tableStore.expandedCellViewWidth) and
            // re-seeds defaultSize when the panel is reopened or the page
            // reloads. The Separator MUST be a direct child of the Group
            // (react-resizable-panels contract), so it sits here as the Panel's
            // sibling inside this fragment.
            //
            // NOTE: unlike the Supervisor / ResourceLib side panels, this Panel
            // must NOT set react-resizable-panels' `disabled` prop. `disabled`
            // renders `aria-disabled="true"` on the panel root, and Playwright
            // (and assistive tech) treat a control nested under an
            // `aria-disabled` ancestor as non-editable — which blocked
            // fill()/typing in the moved cell textarea below.
            <>
              <Separator
                id="expanded-cell-view-resizer"
                className="c-expanded-cell-view-resizer"
              >
                <div className="c-expanded-cell-view-resizer__icon">
                  <img src={svgHandles as unknown as string} alt="" />
                </div>
              </Separator>
              <Panel
                defaultSize={`${tableStore.expandedCellViewWidth}px`}
                minSize={`${EXPANDED_CELL_VIEW_MIN_WIDTH}px`}
                maxSize={`${EXPANDED_CELL_VIEW_MAX_WIDTH}px`}
                onResize={(panelSize) =>
                  debouncedPersistExpandedCellViewWidth(panelSize.inPixels)
                }
                className="c-editor-table__layout-sidepanel"
              >
                <ExpandedCellViewPanel viewOnly={isViewer} />
              </Panel>
            </>
          )}
          {tableStore.keyboardShortcutsOpen && (
            // PSTUDIO-3623: keyboard-shortcuts help panel. Like Expanded Cell
            // View, this Panel must NOT set `disabled` — the SegmentedControl
            // must stay clickable (`aria-disabled` would block Playwright/AT
            // interaction, PSTUDIO-3624). Fixed 500px via minSize===maxSize.
            <Panel
              defaultSize={sidebarSize}
              minSize={sidebarSize}
              maxSize={sidebarSize}
              className="c-editor-table__layout-sidepanel"
            >
              {/* PSTUDIO-3623: the panel reads the lazy `pro-studio/keyboard-shortcuts`
                  i18n namespace (via bare useTranslation() + `pro-studio/keyboard-shortcuts:`-
                  prefixed keys) and mounts on a SYNCHRONOUS toolbar click. react-i18next uses
                  Suspense by default, so a namespace that isn't loaded yet suspends — and a
                  suspend "in response to synchronous input" with no boundary above replaces the
                  WHOLE app with the error boundary. This LOCAL boundary keeps the suspend scoped
                  to the panel (one empty frame, then it renders once the ns resolves). */}
              <Suspense fallback={null}>
                <KeyboardShortcutsPanel />
              </Suspense>
            </Panel>
          )}
        </Group>

        {/* <div className="c-editor-table-view-control">
          <Button
            test-id="ps-editor-table-view-control-btn"
            width="full"
            size="xlarge"
            variant="secondary"
            disabled={
              !tableStore.draftVersion ||
              !tableStore.solutionVersionId ||
              isViewer
            }
            onClick={() =>
              tableStore.setState(
                tableStore.isEditing() ? 'viewing' : 'editing'
              )
            }
          >
            <Icon
              slot="prefix"
              name={tableStore.isEditing() ? 'unlock' : 'lock'}
            />
            {tableStore.isEditing() ? 'Edit Mode' : 'View Mode'}
          </Button>
        </div> */}
      </div>
    </>
  );
});
